/**
 * Compliance — BD-grade compliance workstation.
 *
 * Distinct product surface. $2,500/mo per broker-dealer entity.
 * Scope covers what a Chief Compliance Officer manages daily across
 * ~18 workstreams grouped into 8 domains. Sidebar-driven navigation;
 * each section is its own workspace with real actions and status.
 *
 * The comprehensive scope is intentional — this is what a professional
 * BD compliance team pays for. Every artifact is chain-anchored to the
 * same audit ledger as Attestation + Cohorts + Match Certificates, so
 * regulators can verify integrity without account access. That's the
 * cryptographic moat that legacy compliance platforms (Global Relay,
 * RegEd, Advantage Compliance) don't offer.
 *
 * Backend endpoints are stubbed via `/api/compliance/*`; every section
 * renders an honest empty state until the corresponding endpoint ships.
 * No fake data anywhere.
 */
import { useEffect, useState } from 'react';
import { useStore } from '@/store';
import { API_BASE } from '@/lib/api';
import { describeApiError, type RouterErrorBody } from '@/lib/apiErrors';
import { GRN, RED, ORG } from '@/theme';

const FONT = 'Inter, sans-serif';
const MONO = 'JetBrains Mono, monospace';

// ── Theme (aligned to OmniStrat Stone-palette dark) ────────────────
// See marketing/omnistrat CSS variables — this maps the terminal's
// compliance dashboard onto the same aura used by /agora, /cohorts,
// /matchmaking, /compliance, /taskforces, /admin. Stone palette keeps
// the trader-friendly amber accent (--d4a45a) while replacing pure
// grayscale with warm-neutral tones.
const BG        = '#0c0a09';   // OmniStrat --bg (dark)
const PANEL     = '#1c1917';   // OmniStrat --surface (dark)
const PANEL_HI  = '#292524';   // OmniStrat --surface-soft (dark)
const LINE      = '#44403c';   // OmniStrat --border-mid (dark)
const INK       = '#fafaf9';   // OmniStrat --ink (dark)
const INK_DIM   = '#d6d3d1';   // OmniStrat --ink-soft (dark)
const INK_FAINT = '#78716c';   // OmniStrat --ink-faint (dark)

// ── Types ──────────────────────────────────────────────────────────
type Status = 'ok' | 'due' | 'watch' | 'late' | 'unknown';
type AgencyName = 'SEC' | 'FINRA' | 'INTERNAL' | 'FinCEN' | 'MSRB' | 'CFTC';

// Every section response carries this provenance block (see
// complianceRoutes withProvenance). `configured: false` means nothing
// writes that store — an empty list is an absence of evidence, not a pass.
interface ControlMeta {
  section?:         string;
  configured?:      boolean;
  producer?:        string | null;
  note?:            string;
  last_written_at?: string | null;
}
function controlOf(data: Record<string, unknown> | null): ControlMeta | null {
  return (data?._control as ControlMeta | undefined) ?? null;
}

// Invalidate one or more cached sections so the loader refetches them.
type Invalidate = (...sections: string[]) => void;

// ── API plumbing ───────────────────────────────────────────────────
// One place that knows where the bearer token lives. The read path in
// <Compliance> and every write below share it, so an auth change lands
// in a single spot instead of being copied per call site.
function authToken(): string {
  return sessionStorage.getItem('omnistrat_token') || localStorage.getItem('omnistrat-token') || '';
}
function authHeaders(withJsonBody = false): Record<string, string> {
  const tok = authToken();
  const h: Record<string, string> = tok ? { Authorization: 'Bearer ' + tok } : {};
  if (withJsonBody) h['Content-Type'] = 'application/json';
  return h;
}

// A FAILED WRITE MUST NEVER LOOK LIKE A SUCCESSFUL ONE. Surface the
// server's own `error`/`detail` verbatim; never swallow, never fall
// through to a success state.
async function describeFailure(r: Response): Promise<string> {
  let body: unknown = null;
  try { body = await r.json(); } catch { /* error body was not JSON */ }
  // One vocabulary with the rest of the terminal (lib/apiErrors.ts). The bare
  // code ("DESIGNATIONS_UNAVAILABLE (HTTP 503)") did not say that nothing was
  // done, or that a 503 here is "we could not tell", not "no".
  return `${describeApiError(r.status, body as RouterErrorBody | null).message} (HTTP ${r.status})`;
}
async function apiGet<T>(path: string): Promise<T> {
  const r = await fetch(API_BASE + path, { headers: authHeaders() });
  if (!r.ok) throw new Error(await describeFailure(r));
  return await r.json() as T;
}
async function apiPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const r = await fetch(API_BASE + path, {
    method: 'POST', headers: authHeaders(true), body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await describeFailure(r));
  return await r.json() as T;
}

// ── Section catalog ────────────────────────────────────────────────
// Grouped so the sidebar reads like a real CCO's mental model.
interface SectionDef {
  id:     string;
  label:  string;
  agency?: AgencyName;
  // 'roadmap' = the panel exists but no endpoint is wired yet. Needed so a
  // section that cannot load real data stops advertising itself as 'live'.
  badge?: 'live' | 'daily' | 'quarterly' | 'annual' | 'on-demand' | 'roadmap';
}

// Sections are organized into two explicit tiers:
//
//   FOUNDATION — cross-vertical compliance workflows that apply to any
//     regulated business (fintech, AI, healthcare, legal, payments).
//     Same substrate powers Foundry AI governance and Agora attestation
//     compliance surfaces, not just Terminal BD.
//
//   BROKER-DEALER — vertical extension for firms registered as a BD.
//     Adds CAT, Rule 606/605, FOCUS, Form CRS, Reg SHO, trade surveillance,
//     employee trading, position + cash reconciliation.
//
// The sidebar labels each group with its tier so users immediately see
// which parts of the workstation are cross-vertical vs BD-specific.
const SECTIONS: Array<{ group: string; tier: 'FOUNDATION' | 'BROKER-DEALER'; items: SectionDef[] }> = [
  {
    group: 'Overview', tier: 'FOUNDATION',
    items: [
      { id: 'overview',       label: 'Firm dashboard',        badge: 'live'   },
      { id: 'kill-switch',    label: 'Trading halt',          agency: 'INTERNAL', badge: 'live' },
    ],
  },
  // ── FOUNDATION (cross-vertical) ────────────────────────────────
  {
    group: 'AML / KYC', tier: 'FOUNDATION',
    items: [
      { id: 'ofac',           label: 'Sanctions (OFAC)',   agency: 'FinCEN', badge: 'live'  },
      { id: 'cip-kyc',        label: 'CIP / KYC',          agency: 'FinCEN', badge: 'live'  },
      { id: 'sar',            label: 'SAR management',     agency: 'FinCEN', badge: 'on-demand' },
    ],
  },
  {
    group: 'Records & Archive', tier: 'FOUNDATION',
    items: [
      { id: 'archive-17a4',   label: '17a-4 WORM archive',   agency: 'SEC',   badge: 'live'      },
      { id: 'books-17a3',     label: 'Books & records',      agency: 'SEC',   badge: 'roadmap'   },
      { id: 'blue-sheets',    label: 'Blue Sheets',          agency: 'SEC',   badge: 'on-demand' },
    ],
  },
  {
    group: 'Supervision', tier: 'FOUNDATION',
    items: [
      { id: 'wsps',           label: 'WSPs / policies',      agency: 'FINRA', badge: 'annual'    },
      { id: 'testing',        label: 'Compliance testing',   agency: 'FINRA', badge: 'quarterly' },
      { id: 'complaints',     label: 'Complaint log',        agency: 'FINRA', badge: 'live'      },
    ],
  },
  {
    group: 'Communications', tier: 'FOUNDATION',
    items: [
      { id: 'comms-surv',     label: 'Comms review',         agency: 'INTERNAL', badge: 'daily' },
    ],
  },
  {
    group: 'Personnel', tier: 'FOUNDATION',
    items: [
      { id: 'annual-mtg',     label: 'Annual compliance meeting', agency: 'FINRA', badge: 'annual' },
      { id: 'firm-element',   label: 'Continuing education',      agency: 'FINRA', badge: 'annual' },
    ],
  },
  // ── BROKER-DEALER (vertical extension) ─────────────────────────
  {
    group: 'BD Reporting', tier: 'BROKER-DEALER',
    items: [
      { id: 'cat',            label: 'CAT',            agency: 'FINRA', badge: 'daily'      },
      { id: 'rule606',        label: 'Rule 606',       agency: 'SEC',   badge: 'quarterly'  },
      { id: 'rule605',        label: 'Rule 605',       agency: 'SEC',   badge: 'quarterly'  },
      { id: 'focus',          label: 'FOCUS Report',   agency: 'FINRA', badge: 'quarterly'  },
      { id: 'form-crs',       label: 'Form CRS',       agency: 'SEC',   badge: 'annual'     },
    ],
  },
  {
    group: 'BD Surveillance', tier: 'BROKER-DEALER',
    items: [
      { id: 'trade-surv',     label: 'Trade surveillance',      agency: 'INTERNAL', badge: 'live'  },
      { id: 'emp-trading',    label: 'Employee trading',        agency: 'INTERNAL', badge: 'live'  },
      { id: 'reg-bi',         label: 'Reg BI / suitability',    agency: 'SEC',      badge: 'roadmap'  },
    ],
  },
  {
    group: 'BD Reg SHO', tier: 'BROKER-DEALER',
    items: [
      { id: 'reg-sho',        label: 'Locate log',              agency: 'SEC', badge: 'live'  },
      { id: 'close-out',      label: 'Close-out obligations',   agency: 'SEC', badge: 'daily' },
    ],
  },
  {
    group: 'BD Personnel', tier: 'BROKER-DEALER',
    items: [
      { id: 'restricted',     label: 'Restricted list',           agency: 'INTERNAL', badge: 'live' },
    ],
  },
  {
    group: 'BD Reconciliation', tier: 'BROKER-DEALER',
    items: [
      { id: 'position-recon', label: 'Position recon',    agency: 'INTERNAL', badge: 'daily' },
      { id: 'cash-recon',     label: 'Cash recon',        agency: 'INTERNAL', badge: 'daily' },
    ],
  },
];

// ── Small primitives ───────────────────────────────────────────────

function StatusPill({ status }: { status: Status }) {
  const map: Record<Status, { text: string; bg: string; fg: string }> = {
    ok:      { text: 'OK',      bg: 'rgba(105,176,120,0.15)', fg: GRN },
    due:     { text: 'DUE',     bg: 'rgba(212,164,90,0.18)',  fg: ORG },
    watch:   { text: 'WATCH',   bg: 'rgba(212,164,90,0.10)',  fg: ORG },
    late:    { text: 'LATE',    bg: 'rgba(198,107,107,0.18)', fg: RED },
    unknown: { text: '—',       bg: 'rgba(245,244,238,0.06)', fg: INK_FAINT },
  };
  const s = map[status];
  return (
    <span style={{
      fontFamily: MONO, fontSize: 10, fontWeight: 500,
      padding: '2px 8px', borderRadius: 3,
      background: s.bg, color: s.fg,
      letterSpacing: '0.08em',
    }}>{s.text}</span>
  );
}

function AgencyChip({ agency }: { agency: AgencyName }) {
  const map: Record<AgencyName, string> = {
    SEC:      '#9bc3ff',
    FINRA:    '#e6c574',
    FinCEN:   '#d39bff',
    MSRB:     '#9ae5b0',
    CFTC:     '#74c5e6',
    INTERNAL: INK_FAINT,
  };
  return <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', color: map[agency] }}>{agency}</span>;
}

function BadgeChip({ text }: { text: string }) {
  return (
    <span style={{
      fontFamily: MONO, fontSize: 9, letterSpacing: '0.08em',
      color: INK_FAINT, textTransform: 'uppercase',
      padding: '1px 6px', border: '1px solid ' + LINE, borderRadius: 3,
    }}>{text}</span>
  );
}

function Panel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: PANEL, border: '1px solid ' + LINE, borderRadius: 8,
      padding: '20px 22px', ...(style ?? {}),
    }}>{children}</div>
  );
}

function SectionHead({ title, sub, right }: { title: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 16, marginBottom: 16, paddingBottom: 10,
      borderBottom: '1px solid ' + LINE,
    }}>
      <div>
        <h2 style={{ margin: 0, fontFamily: FONT, fontWeight: 400, fontSize: 18, letterSpacing: '-0.01em', color: INK }}>{title}</h2>
        {sub && <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK_FAINT, marginTop: 3 }}>{sub}</div>}
      </div>
      {right && <div style={{ fontFamily: MONO, fontSize: 11, color: INK_DIM }}>{right}</div>}
    </div>
  );
}

function EmptyState({ line, sub }: { line: string; sub?: string }) {
  // Empty states are intentional — nothing has happened here yet, and
  // that reads as ready-to-work, not under-construction. A subtle
  // status dot replaces the previous PREVIEW badge for a premium feel
  // that matches agora-call.html + index.html across the OmniStrat
  // marketing surface.
  return (
    <div style={{
      padding: '48px 24px', textAlign: 'center', color: INK_FAINT,
      fontFamily: FONT, fontSize: 13, lineHeight: 1.65,
    }}>
      <div style={{
        width: 6, height: 6, borderRadius: '50%', background: INK_FAINT,
        opacity: 0.55, margin: '0 auto 14px',
      }} />
      <div style={{ color: INK_DIM, fontSize: 14 }}>{line}</div>
      {sub && (
        <div style={{
          marginTop: 8, fontSize: 12, opacity: 0.75,
          maxWidth: 480, margin: '8px auto 0', color: INK_FAINT,
        }}>{sub}</div>
      )}
    </div>
  );
}

function KV({ label, value, mono, valueColor }: { label: string; value: React.ReactNode; mono?: boolean; valueColor?: string }) {
  return (
    <div>
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK_FAINT, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: mono ? MONO : FONT, fontSize: mono ? 12 : 22, fontWeight: mono ? 400 : 200, color: valueColor ?? INK, letterSpacing: '-0.015em' }}>{value}</div>
    </div>
  );
}

// The one button in this workstation. `size="sm"` is the in-row variant
// used by adjudication / status-transition actions; `disabled` is what
// stops a double-submit while a write is in flight.
export function ActionBtn({ children, onClick, variant, type, disabled, size, tone }: {
  children: React.ReactNode;
  onClick?:  () => void;
  variant?:  'primary' | 'ghost';
  type?:     'button' | 'submit';
  disabled?: boolean;
  size?:     'sm';
  tone?:     'warn' | 'bad';
}) {
  const isPrimary = variant === 'primary';
  const accent = tone === 'bad' ? RED : tone === 'warn' ? ORG : null;
  const sm = size === 'sm';
  return (
    <button type={type ?? 'button'} onClick={onClick} disabled={disabled} style={{
      padding: sm ? '3px 9px' : '7px 14px',
      borderRadius: 999,
      fontFamily: sm ? MONO : FONT,
      fontSize: sm ? 10 : 12,
      letterSpacing: sm ? '0.06em' : undefined,
      whiteSpace: 'nowrap',
      background: isPrimary ? ORG : 'transparent',
      color: isPrimary ? BG : (accent ?? INK),
      border: '1px solid ' + (isPrimary ? ORG : (accent ?? LINE)),
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.45 : 1,
    }}>{children}</button>
  );
}

// ── Form primitives ────────────────────────────────────────────────
// Deliberately plain: monospace inputs, square corners, no animation.
// This is a regulatory record-entry surface, not a signup flow.

const CTRL: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: BG, color: INK,
  border: '1px solid ' + LINE, borderRadius: 3,
  padding: '7px 9px', fontFamily: MONO, fontSize: 12,
  outline: 'none',
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK_FAINT, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontFamily: MONO, fontSize: 10, color: INK_FAINT, opacity: 0.8, marginTop: 4, lineHeight: 1.45 }}>{hint}</div>}
    </label>
  );
}

function TextField({ label, value, onChange, placeholder, hint, kind }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; hint?: string; kind?: 'text' | 'date' | 'number';
}) {
  return (
    <Field label={label} hint={hint}>
      <input
        type={kind ?? 'text'}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={CTRL}
      />
    </Field>
  );
}

function AreaField({ label, value, onChange, placeholder, hint, rows }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; hint?: string; rows?: number;
}) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={value}
        placeholder={placeholder}
        rows={rows ?? 4}
        onChange={e => onChange(e.target.value)}
        style={{ ...CTRL, resize: 'vertical', lineHeight: 1.55 }}
      />
    </Field>
  );
}

function SelectField<T extends string>({ label, value, options, onChange, hint }: {
  label: string; value: T; options: ReadonlyArray<T>; onChange: (v: T) => void; hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <select value={value} onChange={e => onChange(e.target.value as T)} style={CTRL}>
        {options.map(o => <option key={o} value={o} style={{ background: PANEL }}>{o}</option>)}
      </select>
    </Field>
  );
}

function CheckField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: MONO, fontSize: 11, color: INK_DIM, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ accentColor: ORG, width: 13, height: 13 }} />
      <span>{label}</span>
    </label>
  );
}

function CheckGroup<T extends string>({ label, options, selected, onToggle, hint }: {
  label: string; options: ReadonlyArray<{ value: T; label: string }>;
  selected: T[]; onToggle: (v: T, on: boolean) => void; hint?: string;
}) {
  return (
    <Field label={label} hint={hint}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', padding: '6px 0 2px' }}>
        {options.map(o => (
          <CheckField key={o.value} label={o.label} checked={selected.includes(o.value)} onChange={on => onToggle(o.value, on)} />
        ))}
      </div>
    </Field>
  );
}

function FormMsg({ error, ok }: { error?: string | null; ok?: string | null }) {
  if (!error && !ok) return null;
  const bad = !!error;
  return (
    <div style={{
      fontFamily: MONO, fontSize: 11, lineHeight: 1.5, borderRadius: 3,
      padding: '8px 10px', marginTop: 12,
      border: '1px solid ' + (bad ? '#b00' : LINE),
      background: bad ? 'rgba(187,0,0,0.08)' : 'rgba(105,176,120,0.08)',
      color: bad ? '#ff6b6b' : GRN,
    }}>{bad ? error : ok}</div>
  );
}

function FormGrid({ cols, children }: { cols: number; children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, marginBottom: 12 }}>{children}</div>;
}

function FormShell({ title, note, children, onSubmit, onCancel, busy, error, ok, submitLabel }: {
  title: string; note?: string; children: React.ReactNode;
  onSubmit: () => void; onCancel?: () => void;
  busy: boolean; error: string | null; ok: string | null; submitLabel: string;
}) {
  return (
    <form
      onSubmit={e => { e.preventDefault(); onSubmit(); }}
      style={{ border: '1px solid ' + LINE, borderRadius: 6, background: PANEL_HI, padding: '16px 18px', marginBottom: 16 }}
    >
      <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK_DIM, marginBottom: note ? 5 : 12 }}>{title}</div>
      {note && <div style={{ fontFamily: MONO, fontSize: 10, color: INK_FAINT, lineHeight: 1.5, marginBottom: 14 }}>{note}</div>}
      {children}
      <FormMsg error={error} ok={ok} />
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <ActionBtn type="submit" variant="primary" disabled={busy}>{busy ? 'Working…' : submitLabel}</ActionBtn>
        {onCancel && <ActionBtn onClick={onCancel} disabled={busy}>Cancel</ActionBtn>}
      </div>
    </form>
  );
}

// Single in-flight write per surface: `busy` disables the submit button
// so there is no double-submit, `error` carries the server's own message,
// and `ok` is only ever set after a 2xx.
function useWriter() {
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok,    setOk]    = useState<string | null>(null);
  const run = async (fn: () => Promise<string>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true); setError(null); setOk(null);
    try {
      setOk(await fn());
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  // Client-side validation failure — refuse to submit, say why.
  const reject = (msg: string) => { setError(msg); setOk(null); };
  const clear  = () => { setError(null); setOk(null); };
  return { busy, error, ok, run, reject, clear };
}

// ── Section renderers ──────────────────────────────────────────────

// ── Firm profile ───────────────────────────────────────────────────
// THE SETUP GATE. The obligation calendar is derived from the firm's
// registration and product line; with no profile there is nothing to
// derive it from, so the dashboard renders 0/0 obligations and reads as
// "nothing outstanding". Nothing else in this workstation is meaningful
// until this is set.

type EntityType  = 'bd' | 'ia' | 'msb';
type ProductType = 'equities' | 'options' | 'fixed_income' | 'crypto';

const ENTITY_OPTIONS: ReadonlyArray<{ value: EntityType; label: string }> = [
  { value: 'bd',  label: 'Broker-dealer (BD)' },
  { value: 'ia',  label: 'Investment adviser (IA)' },
  { value: 'msb', label: 'Money services business (MSB)' },
];
const PRODUCT_OPTIONS: ReadonlyArray<{ value: ProductType; label: string }> = [
  { value: 'equities',     label: 'Equities' },
  { value: 'options',      label: 'Options' },
  { value: 'fixed_income', label: 'Fixed income' },
  { value: 'crypto',       label: 'Crypto' },
];

interface FirmProfile {
  entity_types?:    EntityType[];
  fiscal_year_end?: string | null;
  products?:        ProductType[];
  is_carrying?:     boolean;
  updated_at?:      string;
}

function FirmProfileGate({ calendarUnconfigured, onSaved }: { calendarUnconfigured: boolean; onSaved: () => void }) {
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadErr,   setLoadErr]   = useState<string | null>(null);
  const [profile,   setProfile]   = useState<FirmProfile | null>(null);
  const [reloads,   setReloads]   = useState(0);

  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [products,    setProducts]    = useState<ProductType[]>([]);
  const [fye,         setFye]         = useState('');
  const [isCarrying,  setIsCarrying]  = useState(false);
  const [openForm,    setOpenForm]    = useState(false);
  const [dismissed,   setDismissed]   = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const w = useWriter();

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading'); setLoadErr(null);
    (async () => {
      try {
        const d = await apiGet<{ profile: FirmProfile | null }>('/api/compliance/firm-profile');
        if (cancelled) return;
        const p = d.profile ?? null;
        setProfile(p);
        // Prefill the form from whatever is already on record so a
        // re-save never silently drops a field the operator can't see.
        setEntityTypes(p?.entity_types ?? []);
        setProducts(p?.products ?? []);
        setFye(p?.fiscal_year_end ?? '');
        setIsCarrying(!!p?.is_carrying);
        setLoadState('loaded');
      } catch (e) {
        if (cancelled) return;
        setLoadErr((e as Error).message);
        setLoadState('error');
      } finally {
        if (!cancelled) setFirstLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [reloads]);

  // Keeps the selection in the canonical option order and de-duplicated,
  // so what is POSTed matches what is displayed.
  const toggle = <T extends string>(all: ReadonlyArray<{ value: T }>, list: T[], set: (v: T[]) => void) =>
    (v: T, on: boolean) => {
      const next = on ? [...list, v] : list.filter(x => x !== v);
      set(all.map(o => o.value).filter(x => next.includes(x)));
    };

  const submit = () => {
    if (entityTypes.length === 0) {
      w.reject('Select at least one registration type. The obligation calendar is derived from it — BD, IA and MSB carry different filings.');
      return;
    }
    const fyeTrimmed = fye.trim();
    if (fyeTrimmed) {
      const m = /^(\d{2})-(\d{2})$/.exec(fyeTrimmed);
      const mm = m ? Number(m[1]) : 0;
      const dd = m ? Number(m[2]) : 0;
      if (!m || mm < 1 || mm > 12 || dd < 1 || dd > 31) {
        w.reject('Fiscal year end must be MM-DD (for example 12-31).');
        return;
      }
    }
    void w.run(async () => {
      const body: Record<string, unknown> = { entity_types: entityTypes, is_carrying: isCarrying };
      if (fyeTrimmed)        body.fiscal_year_end = fyeTrimmed;
      if (products.length)   body.products        = products;
      await apiPost<{ profile?: FirmProfile }>('/api/compliance/firm-profile', body);
      setOpenForm(false);
      setDismissed(false);
      setReloads(n => n + 1);   // refetch the profile
      onSaved();                // refetch the dashboard / obligation calendar
      return 'Firm profile saved. Recomputing the obligation calendar…';
    });
  };

  // Only the very first read blocks the panel. A re-read after a save
  // keeps the previous summary (and the save confirmation) on screen.
  if (loadState === 'loading' && !firstLoaded) {
    return (
      <Panel style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: MONO, fontSize: 11, color: INK_FAINT }}>Loading firm profile…</div>
      </Panel>
    );
  }

  const needsSetup = profile === null || calendarUnconfigured || loadState === 'error';
  const showForm   = openForm || (profile === null && loadState !== 'error' && !dismissed);

  const summaryRow = profile && (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginTop: 14 }}>
      <KV label="Registrations"   value={(profile.entity_types ?? []).map(t => t.toUpperCase()).join(' · ') || '—'} mono />
      <KV label="Fiscal year end" value={profile.fiscal_year_end || '—'} mono />
      <KV label="Products"        value={(profile.products ?? []).join(' · ') || '—'} mono />
      <KV label="Carrying firm"   value={profile.is_carrying ? 'YES' : 'NO'} mono valueColor={profile.is_carrying ? ORG : INK_DIM} />
    </div>
  );

  return (
    <Panel style={{
      marginBottom: 20,
      border: '1px solid ' + (needsSetup ? '#b00' : LINE),
      background: needsSetup ? 'rgba(187,0,0,0.06)' : PANEL,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{
            fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
            color: needsSetup ? '#ff6b6b' : INK_FAINT,
          }}>
            {profile === null ? 'Setup required — firm profile' : 'Firm profile'}
          </div>
          <div style={{ fontFamily: FONT, fontSize: 12.5, color: INK_DIM, marginTop: 6, lineHeight: 1.6, maxWidth: 720 }}>
            {profile === null
              ? 'No firm profile is on record, so no obligation calendar can be computed. Every "0 obligations" and "up to date" figure on this dashboard reflects an absent profile, not a clean firm. Set your registrations and product line to populate the calendar.'
              : calendarUnconfigured
                ? 'A profile is on record, but the obligation calendar reports itself as unconfigured — nothing is maintaining it yet. Re-save the profile to trigger a recompute; until it reports configured, do not read the tiles below as a supervisory result.'
                : 'Registrations and product line that the obligation calendar is derived from.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {!showForm && (
            <ActionBtn variant={needsSetup ? 'primary' : 'ghost'} onClick={() => { w.clear(); setOpenForm(true); }}>
              {profile === null ? 'Set up firm profile' : 'Edit firm profile'}
            </ActionBtn>
          )}
          {showForm && profile !== null && (
            <ActionBtn onClick={() => { setOpenForm(false); setDismissed(true); w.clear(); }} disabled={w.busy}>Hide</ActionBtn>
          )}
        </div>
      </div>

      {loadState === 'error' && (
        <FormMsg error={`Could not read the firm profile — ${loadErr}. The obligation calendar below cannot be trusted until this loads.`} />
      )}

      {/* Save outcome survives the form collapsing on success. */}
      {!showForm && <FormMsg error={w.error} ok={w.error ? null : w.ok} />}

      {!showForm && summaryRow}

      {showForm && (
        <div style={{ marginTop: 16 }}>
          <FormShell
            title="Firm profile"
            note="Obligations are derived from these answers: BD adds CAT / FOCUS / 606 / 605, IA adds ADV, MSB adds FinCEN registration renewal. A carrying firm additionally picks up 15c3-3 reserve and possession-or-control obligations."
            busy={w.busy} error={w.error} ok={w.ok} submitLabel="Save firm profile"
            onSubmit={submit}
            onCancel={profile === null ? undefined : () => { setOpenForm(false); w.clear(); }}
          >
            <div style={{ marginBottom: 12 }}>
              <CheckGroup
                label="Registration type (required)"
                options={ENTITY_OPTIONS}
                selected={entityTypes}
                onToggle={toggle(ENTITY_OPTIONS, entityTypes, setEntityTypes)}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <CheckGroup
                label="Product line"
                options={PRODUCT_OPTIONS}
                selected={products}
                onToggle={toggle(PRODUCT_OPTIONS, products, setProducts)}
                hint="Drives product-specific obligations (options position reporting, Reg SHO, crypto travel rule)."
              />
            </div>
            <FormGrid cols={2}>
              <TextField label="Fiscal year end" value={fye} onChange={setFye} placeholder="12-31" hint="MM-DD. Sets FOCUS and annual audit due dates." />
              <Field label="Carrying firm">
                <div style={{ padding: '8px 0 2px' }}>
                  <CheckField label="Carries customer funds or securities" checked={isCarrying} onChange={setIsCarrying} />
                </div>
              </Field>
            </FormGrid>
          </FormShell>
        </div>
      )}
    </Panel>
  );
}

// ── Firm-wide sweep ────────────────────────────────────────────────
function SweepControl({ onDone }: { onDone: () => void }) {
  const w = useWriter();
  const [counts, setCounts] = useState<{ alerts: number; locates: number; recon: number } | null>(null);
  // The endpoint may report either counts or the written rows themselves.
  const size = (v: unknown): number => Array.isArray(v) ? v.length : typeof v === 'number' ? v : 0;
  const sweep = () => {
    setCounts(null);
    void w.run(async () => {
      const d = await apiPost<{ alerts?: unknown; locates?: unknown; recon?: unknown }>('/api/compliance/sweep', {});
      const c = { alerts: size(d.alerts), locates: size(d.locates), recon: size(d.recon) };
      setCounts(c);
      // Refetch everything the sweep writes to, plus this dashboard.
      onDone();
      return `Sweep complete — ${c.alerts} surveillance alert(s), ${c.locates} locate record(s), ${c.recon} reconciliation snapshot(s).`;
    });
  };
  return (
    <Panel style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK_FAINT }}>Compliance sweep</div>
          <div style={{ fontFamily: FONT, fontSize: 12.5, color: INK_DIM, marginTop: 6, lineHeight: 1.6, maxWidth: 720 }}>
            Runs the surveillance, locate and reconciliation passes now and writes what they find. Until a sweep has run, the surveillance and reconciliation panels are reporting an absent control, not a clean book.
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <ActionBtn onClick={sweep} disabled={w.busy}>{w.busy ? 'Sweeping…' : 'Run compliance sweep now'}</ActionBtn>
        </div>
      </div>
      {counts && !w.error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 16 }}>
          <KV label="Surveillance alerts" value={String(counts.alerts)} mono valueColor={counts.alerts > 0 ? RED : GRN} />
          <KV label="Locate records"      value={String(counts.locates)} mono />
          <KV label="Recon snapshots"     value={String(counts.recon)} mono />
        </div>
      )}
      <FormMsg error={w.error} ok={w.error ? null : w.ok} />
    </Panel>
  );
}

function Overview({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const obligations = ((data?.obligations as Array<{ id: string; label: string; agency: AgencyName; status: Status; next_due?: string }> | undefined) ?? null);
  // `_control` for this section is the obligation calendar's provenance.
  const calendarUnconfigured = controlOf(data)?.configured === false;
  return (
    <>
      <SectionHead title="Firm compliance dashboard" sub="Everything in one view" right={obligations ? `${obligations.length} obligations` : ''} />

      <FirmProfileGate
        calendarUnconfigured={calendarUnconfigured}
        onSaved={() => invalidate('overview', 'cat', 'rule606', 'rule605', 'focus', 'form-crs', 'firm-element', 'annual-mtg', 'close-out')}
      />
      <SweepControl onDone={() => invalidate('overview', 'trade-surv', 'reg-sho', 'position-recon', 'cash-recon')} />

      {/* Firm-wide status tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Reporting up-to-date" value={obligations === null ? '—' : (obligations.filter(o => o.status === 'ok').length + '/' + obligations.length)} /></Panel>
        <Panel><KV label="Due this week"        value={obligations === null ? '—' : String(obligations.filter(o => o.status === 'due').length)}   valueColor={ORG} /></Panel>
        <Panel><KV label="Overdue"              value={obligations === null ? '—' : String(obligations.filter(o => o.status === 'late').length)}  valueColor={RED} /></Panel>
        <Panel><KV label="Blue Sheets open"     value="—" /></Panel>
      </div>

      <Panel>
        <SectionHead title="Regulatory obligations" sub="What's due next" />
        {obligations === null ? (
          <EmptyState line="Loading obligations..." />
        ) : obligations.length === 0 ? (
          <EmptyState line="No obligations configured" sub="Set up your BD entity's reporting profile. Obligations auto-populate from your firm's registration (BD/IA/MSB) and product line (equities/options/fixed income/crypto)." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 14, rowGap: 8, alignItems: 'center' }}>
            {obligations.map(o => (
              <>
                <div key={o.id + '-l'} style={{ fontSize: 13, color: INK }}>{o.label}</div>
                <div key={o.id + '-a'}><AgencyChip agency={o.agency} /></div>
                <div key={o.id + '-n'} style={{ fontFamily: MONO, fontSize: 11, color: INK_DIM }}>Due {o.next_due?.slice(0, 10) ?? '—'}</div>
                <div key={o.id + '-s'}><StatusPill status={o.status} /></div>
              </>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function CATSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const catEntries = entries?.filter(e => e.label.toLowerCase().includes('cat')) ?? null;
  const now = Date.now();
  const next = catEntries?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const submittedCount = catEntries?.filter(e => e.status === 'filed').length ?? null;
  const overdueCount = catEntries?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  return (
    <>
      <SectionHead title="Consolidated Audit Trail" sub="FINRA · daily submission" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next submission"  value={next ? next.due_at.slice(0, 10) : '—'} mono /></Panel>
        <Panel><KV label="Submitted to date" value={submittedCount === null ? '—' : String(submittedCount)} mono valueColor={GRN} /></Panel>
        <Panel><KV label="Overdue"           value={overdueCount === null ? '—' : String(overdueCount)} valueColor={overdueCount !== null && overdueCount > 0 ? RED : GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Recent submissions" sub="Order events + error resolution" />
        {catEntries === null ? (
          <EmptyState line="Loading submissions..." />
        ) : catEntries.length === 0 ? (
          <EmptyState line="No CAT pipeline connected" sub="PLANNED: order events formatted into CAT messages (MEOR/MEOA/OrderRoute/OrderFulfill) for review before daily submission. No formatter or submission path exists yet." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Due</div><div>Status</div>
            </div>
            {catEntries.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function Rule606Section({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const rule606 = entries?.filter(e => e.label.toLowerCase().includes('606')) ?? null;
  const now = Date.now();
  const nextQ = rule606?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const filedCount = rule606?.filter(e => e.status === 'filed').length ?? null;
  return (
    <>
      <SectionHead title="Rule 606 — Order routing disclosure" sub="SEC · quarterly (holding + customer-directed)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next report due"  value={nextQ ? nextQ.due_at.slice(0, 10) : '—'} mono /></Panel>
        <Panel><KV label="Filed to date"    value={filedCount === null ? '—' : String(filedCount)} mono valueColor={GRN} /></Panel>
        <Panel><KV label="Cadence"          value="Quarterly" mono /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filed reports" sub="Historical 606 disclosures" />
        {rule606 === null ? (
          <EmptyState line="Loading 606 reports..." />
        ) : rule606.length === 0 ? (
          <EmptyState line="No 606 compiler connected" sub="PLANNED: quarterly aggregation of held/not-held routing per venue with payment-for-order-flow disclosure, published and chain-anchored. No compiler or publish step exists yet." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Due</div><div>Status</div>
            </div>
            {rule606.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function Rule605Section({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('rule 605')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"Rule 605 — Order execution quality"} sub={"SEC · monthly (per venue, per order type)"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No 605 compiler connected" sub={"PLANNED: monthly best-execution metrics per market centre and order category. No compiler exists yet."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function FocusSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('focus')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"FOCUS Report"} sub={"FINRA · Rule 15c3-1 net capital + financial condition"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No FOCUS generator connected" sub={"PLANNED: Parts I / II / IIA generated for eFOCUS once trial balance and haircut computations are wired to your accounting system. Not implemented."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function FormCrsSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('form crs')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"Form CRS"} sub={"SEC · customer relationship summary"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No Form CRS delivery tracking connected" sub={"Form CRS must be delivered at account opening, on material change, and on request. PLANNED: timestamped, chain-anchored delivery records. No delivery tracking exists yet."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function TradeSurvSection({ data }: { data: Record<string, unknown> | null }) {
  const alerts = (data?.alerts as Array<{ id: string; detected_at: string; rule: string; symbol: string; account: string; severity: string; reviewed: boolean; disposition?: string }> | undefined) ?? null;
  const now = Date.now();
  const in24h = alerts?.filter(a => now - new Date(a.detected_at).getTime() < 24 * 3600_000) ?? null;
  const cnt = (rule: string) => in24h ? in24h.filter(a => a.rule === rule).length : null;
  const fmt = (n: number | null) => n === null ? '—' : String(n);
  return (
    <>
      <SectionHead title="Trade surveillance" sub="Pattern detection across all order flow" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Spoofing (24h)"       value={fmt(cnt('spoofing'))}       valueColor={(cnt('spoofing') ?? 0) > 0 ? RED : GRN} /></Panel>
        <Panel><KV label="Layering (24h)"       value={fmt(cnt('layering'))}       valueColor={(cnt('layering') ?? 0) > 0 ? RED : GRN} /></Panel>
        <Panel><KV label="Wash trade (24h)"     value={fmt(cnt('wash_sale'))}      valueColor={(cnt('wash_sale') ?? 0) > 0 ? RED : GRN} /></Panel>
        <Panel><KV label="Marking close (24h)"  value={fmt(cnt('marking_close'))}  valueColor={(cnt('marking_close') ?? 0) > 0 ? RED : GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Detection rules" sub="Active surveillance patterns" />
        {alerts === null ? (
          <EmptyState line="Loading alerts..." />
        ) : alerts.length === 0 ? (
          <EmptyState line="No surveillance engine connected" sub="PLANNED: detection for spoofing, layering, wash trading, marking-the-close, momentum ignition and pre-arranged trading, using OMS/OATS timestamps and venue metadata. No rules are currently running and no order flow is being scanned — this panel shows no alerts because nothing is watching, not because none occurred." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Rule</div><div>Symbol</div><div>Account</div><div>Severity</div><div>Reviewed</div><div>When</div>
            </div>
            {alerts.slice(0, 40).map(a => (
              <div key={a.id} style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{a.rule.replace('_', ' ')}</div>
                <div>{a.symbol}</div>
                <div style={{ opacity: 0.75 }}>{a.account.slice(0, 24)}</div>
                <div style={{ color: a.severity === 'critical' ? RED : a.severity === 'high' ? ORG : INK_DIM }}>{a.severity.toUpperCase()}</div>
                <div style={{ color: a.reviewed ? GRN : ORG }}>{a.reviewed ? 'YES' : 'NO'}</div>
                <div>{new Date(a.detected_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function CommsSurvSection({ data }: { data: Record<string, unknown> | null }) {
  const alerts = (data?.alerts as Array<{ id: string; detected_at: string; medium: string; rule: string; severity: string; reviewed: boolean; reviewer?: string; resolved_at?: string }> | undefined) ?? null;
  const now = Date.now();
  const ingested24h = alerts?.filter(a => now - new Date(a.detected_at).getTime() < 24 * 3600_000).length ?? null;
  const flagged = alerts?.filter(a => a.severity === 'high' || a.severity === 'critical').length ?? null;
  const queue = alerts?.filter(a => !a.reviewed).length ?? null;
  const fmt = (n: number | null) => n === null ? '—' : String(n);
  return (
    <>
      <SectionHead title="Communications surveillance" sub="Email, chat, SMS review" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Alerts ingested (24h)"  value={fmt(ingested24h)} mono /></Panel>
        <Panel><KV label="Flagged for review"     value={fmt(flagged)}     valueColor={(flagged ?? 0) > 0 ? ORG : GRN} /></Panel>
        <Panel><KV label="Reviewer queue"         value={fmt(queue)}       mono /></Panel>
      </div>
      <Panel>
        <SectionHead title="Review queue" sub="Lexicon + ML flags awaiting reviewer" />
        {alerts === null ? (
          <EmptyState line="Loading alerts..." />
        ) : alerts.length === 0 ? (
          <EmptyState line="No communications ingest connected" sub="PLANNED: ingest of registered representatives' business communications, lexicon scanning for prohibited terms (guarantees, insider information, promissory language) and ML risk scoring. No ingest is currently running — FINRA 3110(b)(4) review is NOT being performed by this system." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Medium</div><div>Severity</div><div>Rule</div><div>Reviewer</div><div>Detected</div>
            </div>
            {alerts.slice(0, 40).map(a => (
              <div key={a.id} style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{a.medium}</div>
                <div style={{ color: a.severity === 'critical' ? RED : a.severity === 'high' ? ORG : INK_DIM }}>{a.severity.toUpperCase()}</div>
                <div>{a.rule}</div>
                <div style={{ color: a.reviewer ? GRN : ORG }}>{a.reviewer || (a.reviewed ? 'auto' : 'unassigned')}</div>
                <div>{new Date(a.detected_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function EmpTradingSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const requests = (data?.requests as Array<{ id: string; submitted_at: string; employee_id: string; symbol: string; side: string; qty: number; status: string; reviewer?: string }> | undefined) ?? null;
  const pending = requests?.filter(r => r.status === 'pending').length ?? null;
  const uniqueEmployees = requests ? new Set(requests.map(r => r.employee_id)).size : null;
  const fmt = (n: number | null) => n === null ? '—' : String(n);

  const [open,   setOpen]   = useState(false);
  const [emp,    setEmp]    = useState('');
  const [symbol, setSymbol] = useState('');
  const [side,   setSide]   = useState<'buy' | 'sell'>('buy');
  const [qty,    setQty]    = useState('');
  const [notes,  setNotes]  = useState('');
  const w = useWriter();

  const submit = () => {
    if (!emp.trim())    { w.reject('Employee ID is required.'); return; }
    if (!symbol.trim()) { w.reject('Symbol is required.'); return; }
    const n = Number(qty);
    if (qty.trim() === '' || !Number.isFinite(n) || n <= 0) { w.reject('Quantity must be a number greater than 0.'); return; }
    void w.run(async () => {
      const body: Record<string, unknown> = {
        employee_id: emp.trim(),
        symbol:      symbol.trim().toUpperCase(),
        side,
        qty:         n,
      };
      if (notes.trim()) body.notes = notes.trim();
      await apiPost<{ request?: unknown }>('/api/compliance/emp-trading', body);
      setSymbol(''); setQty(''); setNotes('');
      invalidate('emp-trading');
      return 'Pre-clearance request submitted. It is PENDING until a supervisor dispositions it — submission is not approval.';
    });
  };

  return (
    <>
      <SectionHead title="Employee trading" sub="Pre-clearance + restricted list" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Access persons"     value={fmt(uniqueEmployees)} /></Panel>
        <Panel><KV label="Pending requests"   value={fmt(pending)} valueColor={(pending ?? 0) > 0 ? ORG : GRN} /></Panel>
        <Panel><KV label="Total requests"     value={fmt(requests?.length ?? null)} mono /></Panel>
      </div>
      <Panel>
        <SectionHead
          title="Pre-clearance queue"
          right={open ? '' : <ActionBtn onClick={() => { w.clear(); setOpen(true); }}>+ Submit pre-clearance</ActionBtn>}
        />
        {open && (
          <FormShell
            title="Submit pre-clearance request"
            note="Submission is not approval. The request is recorded as PENDING; there is no automatic check against the restricted list, blackout windows or 30-day holding periods — a supervisor must disposition it."
            busy={w.busy} error={w.error} ok={w.ok} submitLabel="Submit request"
            onSubmit={submit}
            onCancel={() => { setOpen(false); w.clear(); }}
          >
            <FormGrid cols={4}>
              <TextField   label="Employee ID (required)" value={emp}    onChange={setEmp}    placeholder="emp_0142" />
              <TextField   label="Symbol (required)"      value={symbol} onChange={setSymbol} placeholder="AAPL" />
              <SelectField label="Side"                   value={side}   options={['buy', 'sell'] as const} onChange={setSide} />
              <TextField   label="Quantity (required)"    value={qty}    onChange={setQty}    placeholder="100" kind="number" />
            </FormGrid>
            <AreaField label="Notes" value={notes} onChange={setNotes} rows={3} placeholder="Account, rationale, or anything a supervisor needs to disposition this." />
          </FormShell>
        )}
        {requests === null ? (
          <EmptyState line="Loading requests..." />
        ) : requests.length === 0 ? (
          <EmptyState line="No pre-clearance requests on record" sub="Use “+ Submit pre-clearance” above to record a request. NOTE: nothing is checked automatically — requests are NOT screened against the restricted list, blackout windows or 30-day holding periods, and no approval workflow runs. A recorded request is not a cleared trade." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Employee</div><div>Symbol</div><div>Side</div><div>Qty</div><div>Status</div><div>When</div>
            </div>
            {requests.slice(0, 40).map(r => (
              <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{r.employee_id}</div>
                <div>{r.symbol}</div>
                <div style={{ color: r.side === 'buy' ? GRN : RED }}>{(r.side ?? '—').toUpperCase()}</div>
                <div>{r.qty}</div>
                <div style={{ color: r.status === 'approved' ? GRN : r.status === 'denied' ? RED : r.status === 'pending' ? ORG : INK_DIM }}>{(r.status ?? 'unknown').toUpperCase()}</div>
                <div>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

// ── OFAC ───────────────────────────────────────────────────────────

interface SdnStatus {
  synced?:     boolean;
  version?:    string | null;
  entries?:    number | null;
  synced_at?:  string | null;
  stale?:      boolean;
  source_url?: string | null;
}

// A screening run against no list cannot assert "clear". This panel is
// what makes that visible before anyone reads a CLEAR verdict below.
function SdnStatusPanel({ onSynced }: { onSynced: () => void }) {
  const [status,  setStatus]  = useState<SdnStatus | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloads, setReloads] = useState(0);
  const w = useWriter();

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadErr(null);
    (async () => {
      try {
        const d = await apiGet<SdnStatus>('/api/compliance/ofac/sdn-status');
        if (!cancelled) setStatus(d);
      } catch (e) {
        if (!cancelled) { setStatus(null); setLoadErr((e as Error).message); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloads]);

  const sync = () => {
    void w.run(async () => {
      const d = await apiPost<{ version?: string | null; entries?: number | null; synced_at?: string | null }>('/api/compliance/ofac/sync', {});
      setReloads(n => n + 1);   // re-read authoritative status
      onSynced();               // screenings carry the list version
      return `SDN list synced — version ${d.version ?? 'unknown'}, ${d.entries ?? 0} entries.`;
    });
  };

  // Unsynced or stale is a red condition: it invalidates every "clear"
  // verdict recorded against it.
  const bad = !loadErr && status !== null && (status.synced === false || status.stale === true);
  return (
    <Panel style={{
      marginBottom: 20,
      border: '1px solid ' + (bad || loadErr ? '#b00' : LINE),
      background: bad || loadErr ? 'rgba(187,0,0,0.06)' : PANEL,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: bad || loadErr ? '#ff6b6b' : INK_FAINT }}>
            OFAC SDN list
          </div>
          {loading ? (
            <div style={{ fontFamily: MONO, fontSize: 11, color: INK_FAINT, marginTop: 6 }}>Loading SDN list status…</div>
          ) : bad ? (
            <div style={{ fontFamily: FONT, fontSize: 12.5, color: INK_DIM, marginTop: 6, lineHeight: 1.6, maxWidth: 720 }}>
              <strong style={{ color: '#ff6b6b' }}>
                {status?.synced === false ? 'NO SDN LIST IS LOADED.' : 'THE LOADED SDN LIST IS STALE.'}
              </strong>{' '}
              A screening run against {status?.synced === false ? 'no list' : 'an out-of-date list'} cannot assert that a customer is clear.
              Sync the list before recording further verdicts, and re-screen anything cleared against it.
            </div>
          ) : (
            <div style={{ fontFamily: FONT, fontSize: 12.5, color: INK_DIM, marginTop: 6, lineHeight: 1.6, maxWidth: 720 }}>
              Treasury SDN list currently loaded for matching.
              {status?.source_url ? ' Source: ' + status.source_url : ''}
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0 }}>
          <ActionBtn variant={bad ? 'primary' : 'ghost'} onClick={sync} disabled={w.busy}>{w.busy ? 'Syncing…' : 'Sync SDN list now'}</ActionBtn>
        </div>
      </div>
      {!loading && !loadErr && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginTop: 16 }}>
          <KV label="Synced"      value={status?.synced === true ? 'YES' : 'NO'} mono valueColor={status?.synced === true ? GRN : RED} />
          <KV label="Version"     value={status?.version || 'no list loaded'} mono />
          <KV label="Entries"     value={status?.entries != null ? status.entries.toLocaleString() : '—'} mono />
          <KV label="Last synced" value={status?.synced_at ? status.synced_at.slice(0, 16).replace('T', ' ') : 'never'} mono
              valueColor={status?.stale === true ? RED : undefined} />
        </div>
      )}
      {loadErr && <FormMsg error={`Could not read SDN list status — ${loadErr}. Do not treat any CLEAR verdict below as verified.`} />}
      <FormMsg error={w.error} ok={w.error ? null : w.ok} />
    </Panel>
  );
}

const OFAC_RESOLUTIONS: ReadonlyArray<{ value: 'false_positive' | 'confirmed' | 'escalated'; label: string; tone?: 'warn' | 'bad' }> = [
  { value: 'false_positive', label: 'FALSE POSITIVE' },
  { value: 'confirmed',      label: 'CONFIRMED', tone: 'bad' },
  { value: 'escalated',      label: 'ESCALATE',  tone: 'warn' },
];

function OfacSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const screenings = (data?.screenings as Array<{ id: string; customer_name: string; screened_at: string; match_type: string; sdn_version: string; resolution?: string; reviewed_by?: string; reviewed_at?: string }> | undefined) ?? null;
  const meta = (data?.meta as { sdn_list_version?: string; sdn_last_synced_at?: string; positive_matches_count?: number; under_review_count?: number } | undefined) ?? {};
  const count = screenings?.length ?? null;

  const [openForm, setOpenForm] = useState(false);
  const [custId,   setCustId]   = useState('');
  const [custName, setCustName] = useState('');
  const screen = useWriter();
  const adj    = useWriter();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const submitScreen = () => {
    if (!custId.trim() || !custName.trim()) {
      screen.reject('Customer ID and customer name are both required — a screening record with no subject is not evidence of a screening.');
      return;
    }
    void screen.run(async () => {
      await apiPost<{ screening?: unknown }>('/api/compliance/ofac/screen', {
        customer_id:   custId.trim(),
        customer_name: custName.trim(),
      });
      // Form stays open with the fields cleared: screening is repetitive
      // work, and the confirmation has to remain visible after the write.
      setCustId(''); setCustName('');
      invalidate('ofac', 'overview');
      return 'Screening recorded and chain-anchored.';
    });
  };

  const adjudicate = (id: string, resolution: 'false_positive' | 'confirmed' | 'escalated') => {
    // Confirming or escalating a sanctions match is a decision that has to
    // be documented; a false positive may be closed with an optional note.
    const required = resolution !== 'false_positive';
    const entered = window.prompt(
      required
        ? `Reason for marking this screening ${resolution.toUpperCase()} (required):`
        : 'Note for this false-positive determination (optional):',
      '',
    );
    if (entered === null) return;                       // operator cancelled
    const note = entered.trim();
    if (required && !note) {
      adj.reject('A note is required when confirming or escalating a sanctions match.');
      return;
    }
    setPendingId(id);
    void adj.run(async () => {
      const body: Record<string, unknown> = { screening_id: id, resolution };
      if (note) body.note = note;
      await apiPost<{ screening?: unknown }>('/api/compliance/ofac/adjudicate', body);
      setPendingId(null);
      invalidate('ofac', 'overview');
      return `Screening adjudicated as ${resolution.replace('_', ' ')}.`;
    }).then(okDone => { if (!okDone) setPendingId(null); });
  };

  return (
    <>
      <SectionHead title="OFAC sanctions screening" sub="FinCEN · SDN, non-SDN, sectoral sanctions" />

      <SdnStatusPanel onSynced={() => invalidate('ofac', 'overview')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Customers screened" value={count === null ? '—' : String(count)} mono /></Panel>
        {/* sdn_last_synced_at is stamped to now() on every screening write
            regardless of whether any list was ever fetched — a freshness
            indicator manufactured from nothing. Label it for what it is. */}
        <Panel><KV label="SDN list version"   value={meta.sdn_list_version ?? 'no list loaded'} mono /></Panel>
        {/* Was unconditionally GRN — 47 positive OFAC hits rendered in
            green as though they were a good result. Colour by value. */}
        <Panel><KV label="Positive matches"   value={meta.positive_matches_count != null ? String(meta.positive_matches_count) : '—'}
                   valueColor={(meta.positive_matches_count ?? 0) > 0 ? RED : GRN} /></Panel>
        <Panel><KV label="Under review"       value={meta.under_review_count    != null ? String(meta.under_review_count)    : '—'} valueColor={ORG} /></Panel>
      </div>
      <Panel>
        <SectionHead
          title="Screening actions"
          right={openForm ? '' : <ActionBtn onClick={() => { screen.clear(); setOpenForm(true); }}>+ Screen a name</ActionBtn>}
        />
        {openForm && (
          <FormShell
            title="Screen a name"
            note="Records a screening against the currently loaded SDN list. The verdict is written by the server; this form does not let you assert one."
            busy={screen.busy} error={screen.error} ok={screen.ok} submitLabel="Run screening"
            onSubmit={submitScreen}
            onCancel={() => { setOpenForm(false); screen.clear(); }}
          >
            <FormGrid cols={2}>
              <TextField label="Customer ID (required)"   value={custId}   onChange={setCustId}   placeholder="cust_00184" hint="Your internal identifier for the subject." />
              <TextField label="Customer name (required)" value={custName} onChange={setCustName} placeholder="Legal name as documented" />
            </FormGrid>
          </FormShell>
        )}
        <FormMsg error={adj.error} ok={adj.error ? null : adj.ok} />
        {screenings === null ? (
          <EmptyState line="Loading screenings..." />
        ) : screenings.length === 0 ? (
          <EmptyState line="No screenings recorded" sub="Use “+ Screen a name” above to record a screening. Note that the MATCH VERDICT IS SUPPLIED BY THE CALLER — this system does not perform fuzzy matching or resolve 50%-rule ownership. Treat entries here as a record of your screening, not as the screening itself." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12, marginTop: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr auto auto auto 1.2fr auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Customer</div><div>SDN version</div><div>Match</div><div>Screened at</div><div>Adjudication</div><div>Action</div>
            </div>
            {screenings.slice(0, 30).map((s) => (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '1.4fr auto auto auto 1.2fr auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)', alignItems: 'center' }}>
                <div style={{ color: INK }}>{s.customer_name}</div>
                <div>{s.sdn_version}</div>
                <div style={{ color: s.match_type === 'positive' ? RED : s.match_type === 'possible' ? ORG : GRN }}>{s.match_type.toUpperCase()}</div>
                <div>{new Date(s.screened_at).toLocaleDateString()}</div>
                {/* Once adjudicated, WHO decided and WHAT they decided is
                    the record — a cleared false positive that nobody
                    signed is not a resolution. */}
                <div>
                  {s.resolution ? (
                    <>
                      <span style={{ color: s.resolution === 'false_positive' ? GRN : s.resolution === 'confirmed' ? RED : ORG }}>
                        {s.resolution.replace('_', ' ').toUpperCase()}
                      </span>
                      <div style={{ fontSize: 10, color: INK_FAINT, marginTop: 2 }}>
                        by {s.reviewed_by || 'unattributed'}
                        {s.reviewed_at ? ' · ' + s.reviewed_at.slice(0, 10) : ''}
                      </div>
                    </>
                  ) : (
                    <span style={{ color: ORG }}>UNRESOLVED</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  {s.resolution ? (
                    <span style={{ fontSize: 10, color: INK_FAINT }}>—</span>
                  ) : OFAC_RESOLUTIONS.map(r => (
                    <ActionBtn
                      key={r.value}
                      size="sm"
                      tone={r.tone}
                      disabled={adj.busy}
                      onClick={() => adjudicate(s.id, r.value)}
                    >{adj.busy && pendingId === s.id ? '…' : r.label}</ActionBtn>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

const ID_TYPES: ReadonlyArray<'passport' | 'drivers_license' | 'ssn' | 'ein' | 'other'> =
  ['passport', 'drivers_license', 'ssn', 'ein', 'other'];
const CIP_METHODS: ReadonlyArray<'documentary' | 'non_documentary' | 'both'> =
  ['documentary', 'non_documentary', 'both'];

function CipKycSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const customers = (data?.customers as Array<{ id: string; legal_name: string; risk_tier: string; onboarded_at: string; cip_completed: boolean; next_review: string }> | undefined) ?? null;
  const total = customers?.length ?? null;
  const highRisk = customers?.filter(c => c.risk_tier === 'high').length ?? null;
  const pending = customers?.filter(c => !c.cip_completed).length ?? null;

  const [open,    setOpen]    = useState(false);
  const [name,    setName]    = useState('');
  const [dob,     setDob]     = useState('');
  const [idType,  setIdType]  = useState<typeof ID_TYPES[number]>('passport');
  const [last4,   setLast4]   = useState('');
  const [country, setCountry] = useState('US');
  const [method,  setMethod]  = useState<typeof CIP_METHODS[number]>('documentary');
  const w = useWriter();

  const submit = () => {
    if (!name.trim()) { w.reject('Legal name is required.'); return; }
    if (last4 && !/^\d{4}$/.test(last4.trim())) { w.reject('ID last 4 must be exactly four digits, or left blank.'); return; }
    void w.run(async () => {
      const body: Record<string, unknown> = {
        legal_name: name.trim(),
        id_type:    idType,
        cip_method: method,
      };
      if (dob.trim())     body.dob_masked      = dob.trim();
      if (last4.trim())   body.id_last4        = last4.trim();
      if (country.trim()) body.address_country = country.trim().toUpperCase();
      await apiPost<{ record?: unknown }>('/api/compliance/cip/register', body);
      setName(''); setDob(''); setLast4('');
      invalidate('cip-kyc', 'overview');
      return 'Customer registered and chain-anchored.';
    });
  };

  return (
    <>
      <SectionHead title="CIP / KYC" sub="FinCEN · customer identification + due diligence" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Customers on record"       value={total    === null ? '—' : String(total)} /></Panel>
        <Panel><KV label="CIP verification pending"  value={pending  === null ? '—' : String(pending)} valueColor={ORG} /></Panel>
        <Panel><KV label="EDD (high risk)"           value={highRisk === null ? '—' : String(highRisk)} mono /></Panel>
      </div>
      <Panel>
        <SectionHead
          title="Customer due diligence workflow"
          right={open ? '' : <ActionBtn onClick={() => { w.clear(); setOpen(true); }}>+ Register customer</ActionBtn>}
        />
        {open && (
          <FormShell
            title="Register customer (CIP)"
            note="Records the identifying information collected and the method used to verify it. Records are chain-anchored on write. NOTE: names are stored in plaintext, not encrypted — do not enter data you are not willing to hold that way. Mask the date of birth."
            busy={w.busy} error={w.error} ok={w.ok} submitLabel="Register customer"
            onSubmit={submit}
            onCancel={() => { setOpen(false); w.clear(); }}
          >
            <FormGrid cols={2}>
              <TextField label="Legal name (required)" value={name} onChange={setName} placeholder="As it appears on the identifying document" />
              <TextField label="Date of birth (masked)" value={dob} onChange={setDob} placeholder="19xx-xx-14" hint="Masked form only." />
            </FormGrid>
            <FormGrid cols={4}>
              <SelectField label="ID type"    value={idType}  options={ID_TYPES}    onChange={setIdType} />
              <TextField   label="ID last 4"  value={last4}   onChange={setLast4}   placeholder="4821" />
              <TextField   label="Country"    value={country} onChange={setCountry} placeholder="US" hint="ISO alpha-2/3." />
              <SelectField label="CIP method" value={method}  options={CIP_METHODS} onChange={setMethod} />
            </FormGrid>
          </FormShell>
        )}
        {customers === null ? (
          <EmptyState line="Loading customers..." />
        ) : customers.length === 0 ? (
          <EmptyState line="No customers on record" sub="Use “+ Register customer” above to record a CIP verification. Records are chain-anchored on write. NOTE: customer names are currently stored in plaintext, not encrypted — do not enter data you are not willing to hold that way." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr auto auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Customer</div><div>Risk</div><div>CIP</div><div>Onboarded</div><div>Next review</div>
            </div>
            {customers.slice(0, 30).map(c => (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1.6fr auto auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{c.legal_name}</div>
                <div style={{ color: c.risk_tier === 'high' ? RED : c.risk_tier === 'medium' ? ORG : GRN }}>{(c.risk_tier ?? 'unrated').toUpperCase()}</div>
                <div style={{ color: c.cip_completed ? GRN : ORG }}>{c.cip_completed ? '✓' : '…'}</div>
                <div>{c.onboarded_at ? new Date(c.onboarded_at).toLocaleDateString() : '—'}</div>
                <div>{c.next_review ? new Date(c.next_review).toLocaleDateString() : '—'}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

const SAR_ACTIVITIES: ReadonlyArray<'structuring' | 'layering' | 'wire_fraud' | 'insider' | 'market_manip' | 'other'> =
  ['structuring', 'layering', 'wire_fraud', 'insider', 'market_manip', 'other'];

// The only transitions this UI offers, and the only words it uses for
// them. A draft is a draft — nothing here calls an unsubmitted SAR
// "filed".
const SAR_NEXT: Record<string, { to: 'submitted' | 'acknowledged' | 'closed'; label: string; tone?: 'warn' | 'bad' }> = {
  draft:        { to: 'submitted',    label: 'MARK SUBMITTED',    tone: 'warn' },
  submitted:    { to: 'acknowledged', label: 'MARK ACKNOWLEDGED' },
  acknowledged: { to: 'closed',       label: 'CLOSE' },
};

function SarSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const filings = (data?.filings as Array<{ id: string; subject_name: string; activity_type: string; amount_usd: number; status: string; created_at: string; fincen_ref?: string }> | undefined) ?? null;
  const openCount   = filings?.filter(f => f.status === 'draft' || f.status === 'submitted').length ?? null;
  // Was counting EVERY filing created this year regardless of status, and
  // every filing is permanently a draft (there is no file/submit endpoint,
  // so `filed_at` is never written). So "SARs filed YTD" reported drafts
  // as filed — on the one artifact where the distinction is the violation.
  // Status union is draft | submitted | acknowledged | closed. Anything
  // past 'draft' has left the building; 'filed' is not a real value.
  const thisYear = new Date().getFullYear();
  const inYear = (d?: string) => !!d && new Date(d).getFullYear() === thisYear;
  const FILED_STATES = ['submitted', 'acknowledged', 'closed'];
  const filedYtd  = filings?.filter(f => inYear(f.created_at) && FILED_STATES.includes(f.status)).length ?? null;
  const draftsYtd = filings?.filter(f => inYear(f.created_at) && f.status === 'draft').length ?? null;

  // FinCEN's 30-day clock runs from detection on the OLDEST open draft.
  // `filings.find(...)` returned the NEWEST one (the list is newest-first
  // from prependToList), so the tile always displayed the most time
  // remaining — with an old draft at day 29 and a new one at day 1 it
  // showed 29 days. The variable was even named `earliestDraft` while
  // doing the opposite. Take the true minimum.
  const oldestDraft = (filings ?? [])
    .filter(f => f.status === 'draft' && f.created_at)
    .reduce<{ created_at: string } | null>((oldest, f) =>
      !oldest || new Date(f.created_at).getTime() < new Date(oldest.created_at).getTime() ? f : oldest, null);
  const daysLeft = oldestDraft
    ? Math.max(0, 30 - Math.floor((Date.now() - new Date(oldestDraft.created_at).getTime()) / 86400_000))
    : null;

  const [open,      setOpen]      = useState(false);
  const [subject,   setSubject]   = useState('');
  const [activity,  setActivity]  = useState<typeof SAR_ACTIVITIES[number]>('structuring');
  const [amount,    setAmount]    = useState('');
  const [narrative, setNarrative] = useState('');
  const create = useWriter();
  const status = useWriter();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const submit = () => {
    if (!subject.trim())   { create.reject('Subject name is required.'); return; }
    if (!narrative.trim()) { create.reject('A narrative is required — a SAR without one cannot be filed.'); return; }
    const amt = Number(amount);
    if (amount.trim() === '' || !Number.isFinite(amt) || amt < 0) {
      create.reject('Amount must be a number of 0 or more.');
      return;
    }
    void create.run(async () => {
      await apiPost<{ filing?: unknown }>('/api/compliance/sar/create', {
        subject_name:  subject.trim(),
        activity_type: activity,
        amount_usd:    amt,
        narrative:     narrative.trim(),
      });
      setSubject(''); setAmount(''); setNarrative('');
      invalidate('sar', 'overview');
      return 'SAR draft created and chain-anchored. It is a DRAFT — transmission to FinCEN is a manual, off-platform step.';
    });
  };

  const advance = (id: string, to: 'submitted' | 'acknowledged' | 'closed') => {
    const body: Record<string, unknown> = { id, status: to };
    if (to === 'submitted') {
      // A submission is only real once it has been transmitted off-platform;
      // the FinCEN acknowledgement reference is the evidence of that.
      const ref = window.prompt('FinCEN BSA identifier for this submission (leave blank if not yet issued):', '');
      if (ref === null) return;                          // operator cancelled
      if (ref.trim()) body.fincen_ref = ref.trim();
    }
    setPendingId(id);
    void status.run(async () => {
      await apiPost<{ filing?: unknown }>('/api/compliance/sar/status', body);
      setPendingId(null);
      invalidate('sar', 'overview');
      return `SAR marked ${to}.`;
    }).then(done => { if (!done) setPendingId(null); });
  };

  return (
    <>
      <SectionHead title="SAR management" sub="FinCEN · Suspicious Activity Report" />
      {/* 4 tiles since drafts were split out from filed. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Open cases"       value={openCount === null ? '—' : String(openCount)} mono /></Panel>
        {/* Filed vs drafted, stated separately. Nothing can currently
            reach "filed" — there is no transmission to FinCEN — so this
            will read 0 filed / N drafts until that exists. That is the
            honest picture. */}
        <Panel><KV label="SARs filed YTD"   value={filedYtd === null ? '—' : String(filedYtd)} mono
                   valueColor={filedYtd === 0 && (draftsYtd ?? 0) > 0 ? ORG : undefined} /></Panel>
        <Panel><KV label="Drafts (not filed)" value={draftsYtd === null ? '—' : String(draftsYtd)} mono
                   valueColor={(draftsYtd ?? 0) > 0 ? ORG : undefined} /></Panel>
        <Panel><KV label="30-day deadline"  value={daysLeft   === null ? '—' : daysLeft + ' d'} valueColor={daysLeft != null && daysLeft <= 5 ? RED : ORG} /></Panel>
      </div>
      <Panel>
        <SectionHead
          title="Investigation queue"
          right={open ? '' : <ActionBtn onClick={() => { create.clear(); setOpen(true); }}>+ New SAR draft</ActionBtn>}
        />
        {open && (
          <FormShell
            title="New SAR draft"
            note="Creates a DRAFT only. This system does not transmit to FinCEN — filing is a manual, off-platform step, and the 30-day clock from detection is yours to manage. The narrative is hashed and chain-anchored on write."
            busy={create.busy} error={create.error} ok={create.ok} submitLabel="Create draft"
            onSubmit={submit}
            onCancel={() => { setOpen(false); create.clear(); }}
          >
            <FormGrid cols={3}>
              <TextField   label="Subject name (required)" value={subject}  onChange={setSubject} placeholder="Subject of the report" />
              <SelectField label="Activity type"           value={activity} options={SAR_ACTIVITIES} onChange={setActivity} />
              <TextField   label="Amount (USD, required)"  value={amount}   onChange={setAmount} placeholder="0" kind="number" />
            </FormGrid>
            <AreaField
              label="Narrative (required)"
              value={narrative}
              onChange={setNarrative}
              rows={6}
              placeholder="Who, what, when, where and why the activity is suspicious."
              hint="FinCEN expects the narrative to stand alone. Only its SHA-256 hash is anchored; retain your own copy."
            />
          </FormShell>
        )}
        <FormMsg error={status.error} ok={status.error ? null : status.ok} />
        {filings === null ? (
          <EmptyState line="Loading filings..." />
        ) : filings.length === 0 ? (
          <EmptyState line="No SAR drafts on record" sub="Use “+ New SAR draft” above to open a case. Drafts are chain-anchored. There is NO automatic red-flag detection, and no transmission to FinCEN — filing remains a manual, off-platform step. The 30-day clock is yours to manage." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12, marginTop: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr auto auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Subject</div><div>Activity</div><div>Amount</div><div>Status</div><div>Created</div><div>Action</div>
            </div>
            {filings.slice(0, 30).map(f => {
              const next = SAR_NEXT[f.status];
              return (
                <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr auto auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)', alignItems: 'center' }}>
                  <div style={{ color: INK }}>{f.subject_name}</div>
                  <div>{(f.activity_type ?? 'other').replace('_', ' ')}</div>
                  <div>{Number.isFinite(f.amount_usd as number) ? `$${(f.amount_usd as number).toLocaleString()}` : '--'}</div>
                  <div style={{ color: f.status === 'draft' ? ORG : f.status === 'submitted' ? '#9bc3ff' : GRN }}>
                    {(f.status ?? 'unknown').toUpperCase()}
                    {f.fincen_ref && <div style={{ fontSize: 10, color: INK_FAINT, marginTop: 2 }}>ref {f.fincen_ref}</div>}
                  </div>
                  <div>{f.created_at ? new Date(f.created_at).toLocaleDateString() : '—'}</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {next ? (
                      <ActionBtn size="sm" tone={next.tone} disabled={status.busy} onClick={() => advance(f.id, next.to)}>
                        {status.busy && pendingId === f.id ? '…' : next.label}
                      </ActionBtn>
                    ) : <span style={{ fontSize: 10, color: INK_FAINT }}>—</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </>
  );
}

function RegShoSection({ data }: { data: Record<string, unknown> | null }) {
  const locates = (data?.locates as Array<{ id: string; requested_at: string; symbol: string; qty: number; source: string; locate_ref: string; status: string; closed_at?: string }> | undefined) ?? null;
  return (
    <>
      <SectionHead title="Reg SHO — Locate log" sub="SEC — short-sale compliance" />
      <Panel>
        {locates === null ? (
          <EmptyState line="Loading locate log..." />
        ) : locates.length === 0 ? (
          <EmptyState line="No locate log connected" sub="Reg SHO requires a locate before a short sale: shares reasonably believed available for delivery. PLANNED: the order path writes the locate log before accepting the order. That wiring does not exist yet — this log is not being populated." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto 1fr 1fr auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Requested</div><div>Symbol</div><div>Qty</div><div>Source</div><div>Locate ref</div><div>Status</div>
            </div>
            {locates.slice(0, 40).map(l => (
              <div key={l.id} style={{ display: 'grid', gridTemplateColumns: 'auto auto auto 1fr 1fr auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div>{l.requested_at.slice(0, 10)}</div>
                <div style={{ color: INK }}>{l.symbol}</div>
                <div>{l.qty.toLocaleString()}</div>
                <div>{l.source}</div>
                <div style={{ opacity: 0.75 }}>{l.locate_ref}</div>
                <div style={{ color: l.status === 'located' ? GRN : l.status === 'closed_out' ? INK_DIM : RED }}>{l.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function CloseOutSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('close-out')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"Close-out obligations"} sub={"SEC · Rule 204 T+2 close-outs"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No close-out tracking connected" sub={"PLANNED: close-out tracking for CNS fails and long/short breaks, with threshold-security monitoring. Not implemented."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function ArchiveSection({ data }: { data: Record<string, unknown> | null }) {
  // Backend returns { archive: ArchiveEntry[], total: number }.
  const archive = (data?.archive as Array<{ id: string; archived_at: string; source: string; size_bytes: number; content_hash: string; retention_until: string }> | undefined) ?? null;
  const total = archive?.length ?? null;
  const totalBytes = archive?.reduce((a, e) => a + (e.size_bytes ?? 0), 0) ?? 0;
  const totalGb = totalBytes / 1_000_000_000;
  const oldest = archive && archive.length > 0
    ? archive.slice().sort((a, b) => a.archived_at.localeCompare(b.archived_at))[0]
    : null;
  return (
    <>
      <SectionHead
        title="17a-4 WORM archive"
        sub="SEC · Rule 17a-4 electronic record retention"
        // "WORM verified" was conditioned on nothing but row count > 0.
        // Nothing is verified: there is no WORM store, no object lock, no
        // retention lock, and no verification step. A green badge asserting
        // an immutability property the system does not implement is the
        // kind of claim that ends badly in an examination.
        right={<span style={{ color: ORG, fontFamily: MONO, fontSize: 11 }}>NOT WORM — no immutable store wired</span>}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Storage"      value={archive === null ? '—' : totalGb < 0.001 ? '0 GB' : totalGb.toFixed(3) + ' GB'} /></Panel>
        <Panel><KV label="Records"      value={total === null ? '—' : total.toLocaleString()} /></Panel>
        {/* Was a hardcoded "7 years" read from nothing. The actual
            storage is KV with a TTL that DELETES, plus a 500-record cap
            that silently drops the oldest entries. Stating a retention
            period we do not enforce is worse than stating none. */}
        <Panel><KV label="Retention"    value="not enforced" mono /></Panel>
        <Panel><KV label="Period start" value={oldest ? oldest.archived_at.slice(0, 10) : '—'} mono /></Panel>
      </div>
      <Panel>
        <SectionHead title="Recent archive events" />
        {archive === null ? (
          <EmptyState line="Loading archive..." />
        ) : archive.length === 0 ? (
          <EmptyState line="No archive writer connected" sub="PLANNED: emails, chat messages, trade confirmations and orders written to a WORM store with a retention lock and chain anchoring on ingest. No archival writer and no WORM bucket are wired today. Do not represent this as a 17a-4 archive." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Source</div><div>Size</div><div>Content hash</div><div>Retained until</div>
            </div>
            {archive.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.source}</div>
                <div>{(e.size_bytes / 1024).toFixed(1)} KB</div>
                <div style={{ opacity: 0.7 }}>{e.content_hash.slice(0, 24)}…</div>
                <div>{e.retention_until.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function BooksSection({ data }: { data: Record<string, unknown> | null }) {
  const archive = (data?.archive as Array<{ id: string; source: string; archived_at: string; size_bytes: number }> | undefined) ?? null;
  const bySource = archive
    ? archive.reduce((acc, e) => { acc[e.source] = (acc[e.source] || 0) + 1; return acc; }, {} as Record<string, number>)
    : null;
  const catalog = [
    ['General ledger', 'trade'],
    ['Trade blotter', 'trade'],
    ['Purchase & sale blotter', 'trade'],
    ['Receipt & delivery blotter', 'trade'],
    ['Securities record (stock record)', 'trade'],
    ['Customer account records', 'order'],
    ['Order tickets (memos)', 'order'],
    ['Confirmations', 'trade'],
    ['Trial balances (monthly)', 'other'],
    ['Net capital computations', 'other'],
    ['Reserve formula computations', 'other'],
    ['Customer complaints', 'email'],
    ['Written agreements', 'other'],
    ['Board resolutions', 'other'],
  ] as const;
  return (
    <>
      <SectionHead title="Books & records" sub="SEC · Rule 17a-3 required records" right={archive && archive.length > 0 ? <span style={{ color: GRN, fontFamily: MONO, fontSize: 11 }}>● {archive.length.toLocaleString()} archived</span> : ''} />
      <Panel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 13 }}>
          {catalog.map(([label, srcKey], i) => {
            const count = bySource ? (bySource[srcKey] ?? 0) : null;
            const status: 'ok' | 'unknown' | 'watch' = count === null ? 'unknown' : count > 0 ? 'ok' : 'watch';
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: PANEL_HI, borderRadius: 6, gap: 12 }}>
                <span style={{ color: INK }}>{label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: count === null ? INK_FAINT : count > 0 ? INK_DIM : INK_FAINT }}>{count === null ? '—' : count.toLocaleString()}</span>
                  <StatusPill status={status} />
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </>
  );
}

function BlueSheetsSection({ data }: { data: Record<string, unknown> | null }) {
  // Backend returns { requests: BlueSheet[] } — not rows. Also uses
  // regulator instead of agency + period_start/period_end + symbols.
  const requests = (data?.requests as Array<{ id: string; regulator: string; request_ref: string; received_at: string; period_start: string; period_end: string; symbols: string[]; status: string; submitted_at?: string }> | undefined) ?? null;
  return (
    <>
      <SectionHead title="Blue Sheets" sub="SEC / FINRA · trade detail request response" />
      <Panel>
        {requests === null ? (
          <EmptyState line="Loading Blue Sheet requests..." />
        ) : requests.length === 0 ? (
          <EmptyState line="No open Blue Sheet requests" sub="Regulatory subpoenas requesting trade detail. PLANNED: response generation from an archive, delivered in SEC Rule 17a-25 electronic format. No archive and no generator exist, chain-anchored for tamper-evidence." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE, fontFamily: MONO }}>
              <div>Agency</div><div>Request ref</div><div>Period</div><div>Symbols</div><div>Status</div>
            </div>
            {requests.map(bs => (
              <div key={bs.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <AgencyChip agency={(bs.regulator === 'FINRA' || bs.regulator === 'SEC') ? bs.regulator as AgencyName : 'INTERNAL'} />
                <div style={{ fontFamily: MONO, fontSize: 12, color: INK }}>{bs.request_ref || bs.id.slice(0, 16)}</div>
                <div style={{ fontFamily: MONO, fontSize: 11, color: INK_DIM }}>
                  {bs.period_start.slice(0, 10)} → {bs.period_end.slice(0, 10)}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 11, color: INK_DIM }}>
                  {bs.symbols.length} sym
                </div>
                <StatusPill status={bs.status === 'submitted' || bs.status === 'acknowledged' ? 'ok' : bs.status === 'in_progress' ? 'watch' : 'due'} />
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

const WSP_STATUSES: ReadonlyArray<'draft' | 'approved' | 'archived'> = ['draft', 'approved', 'archived'];

function WspsSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const policies = (data?.policies as Array<{ id: string; name?: string; title?: string; category?: string; section_ref?: string; version: string; reviewed_at: string; next_review?: string; status?: string }> | undefined) ?? null;
  const currentVer = policies && policies.length > 0 ? policies[0].version : null;
  const lastReview = policies && policies.length > 0
    ? policies.map(p => p.reviewed_at).sort().slice(-1)[0]
    : null;
  const now = Date.now();
  const dueForUpdate = policies?.filter(p => !!p.next_review && new Date(p.next_review).getTime() < now).length ?? null;

  const [open,    setOpen]    = useState(false);
  const [id,      setId]      = useState('');
  const [title,   setTitle]   = useState('');
  const [ref,     setRef]     = useState('');
  const [status,  setStatus]  = useState<typeof WSP_STATUSES[number]>('approved');
  const [content, setContent] = useState('');
  const w = useWriter();

  const openBlank = () => { w.clear(); setId(''); setTitle(''); setRef(''); setStatus('approved'); setContent(''); setOpen(true); };
  const openRevise = (p: { id: string; name?: string; title?: string; category?: string; section_ref?: string; status?: string }) => {
    w.clear();
    setId(p.id);
    setTitle(p.title ?? p.name ?? '');
    setRef(p.section_ref ?? p.category ?? '');
    setStatus((WSP_STATUSES as ReadonlyArray<string>).includes(p.status ?? '') ? (p.status as typeof WSP_STATUSES[number]) : 'approved');
    setContent('');
    setOpen(true);
  };

  const submit = () => {
    if (!title.trim())   { w.reject('Policy title is required.'); return; }
    if (!ref.trim())     { w.reject('A rule / section reference is required — a WSP section that cites nothing supervises nothing.'); return; }
    if (!content.trim()) { w.reject('Policy content is required. Only its hash is retained, so it must be supplied in full on every revision.'); return; }
    void w.run(async () => {
      const body: Record<string, unknown> = {
        title:       title.trim(),
        section_ref: ref.trim(),
        content:     content.trim(),
        status,
      };
      if (id.trim()) body.id = id.trim();
      await apiPost<{ policy?: unknown }>('/api/compliance/wsps/upsert', body);
      setId(''); setTitle(''); setRef(''); setContent('');
      invalidate('wsps');
      return 'Policy recorded and chain-anchored. The effective date is server-set to now — this system does not permit back-dating a WSP.';
    });
  };

  return (
    <>
      <SectionHead title="Written Supervisory Procedures" sub="FINRA Rule 3110 — living document + review cycle" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Current version" value={currentVer ?? '—'} mono /></Panel>
        <Panel><KV label="Last annual review" value={lastReview ? lastReview.slice(0, 10) : '—'} mono /></Panel>
        <Panel><KV label="Sections needing update" value={dueForUpdate === null ? '—' : String(dueForUpdate)} valueColor={(dueForUpdate ?? 0) > 0 ? ORG : GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead
          title="WSP catalog"
          sub="Coverage by regulatory topic"
          right={open ? '' : <ActionBtn onClick={openBlank}>+ Add policy</ActionBtn>}
        />
        {open && (
          <FormShell
            title={id ? 'Revise policy ' + id : 'Add policy'}
            note="The effective and review dates are server-set — this system does not permit back-dating a supervisory procedure. Only the SHA-256 hash of the content is retained, so the full text must be supplied on every revision and kept by you."
            busy={w.busy} error={w.error} ok={w.ok} submitLabel={id ? 'Save revision' : 'Add policy'}
            onSubmit={submit}
            onCancel={() => { setOpen(false); w.clear(); }}
          >
            <FormGrid cols={3}>
              <TextField   label="Title (required)"       value={title} onChange={setTitle} placeholder="Supervision of outside business activities" />
              <TextField   label="Rule reference (required)" value={ref} onChange={setRef} placeholder="FINRA 3110(b)" />
              <SelectField label="Status"                 value={status} options={WSP_STATUSES} onChange={setStatus} />
            </FormGrid>
            <AreaField
              label="Policy content (required)"
              value={content}
              onChange={setContent}
              rows={7}
              placeholder="The procedure as written: who supervises, what they review, how often, and what evidence is retained."
            />
          </FormShell>
        )}
        {policies === null ? (
          <EmptyState line="Loading policies..." />
        ) : policies.length === 0 ? (
          <EmptyState line="WSP catalog not yet configured" sub="Use “+ Add policy” above to record a procedure. Living procedures document with per-section rule references, review cadence and version history. Every material regulatory change should trigger a revision." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12, marginTop: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Policy</div><div>Rule ref</div><div>Version</div><div>Next review</div><div>Status</div><div>Action</div>
            </div>
            {policies.slice(0, 50).map(p => (
              <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)', alignItems: 'center' }}>
                <div style={{ color: INK }}>{p.title ?? p.name ?? '—'}</div>
                <div>{p.section_ref ?? p.category ?? '—'}</div>
                <div>{p.version}</div>
                <div>{p.next_review ? p.next_review.slice(0, 10) : '—'}</div>
                <div style={{ color: p.status === 'approved' ? GRN : p.status === 'draft' ? ORG : INK_DIM }}>{(p.status ?? 'unknown').toUpperCase()}</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionBtn size="sm" disabled={w.busy} onClick={() => openRevise(p)}>REVISE</ActionBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function TestingSection({ data }: { data: Record<string, unknown> | null }) {
  const runs = (data?.runs as Array<{ id: string; run_at: string; scope: string; tests_run: number; passing: number; failing: number; next_run: string }> | undefined) ?? null;
  const lastRun = runs && runs.length > 0 ? runs[0] : null;
  const nextRun = lastRun?.next_run;
  return (
    <>
      <SectionHead title="Compliance testing" sub="FINRA Rule 3120 — supervisory system testing" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Last run"   value={lastRun ? lastRun.run_at.slice(0, 10) : '—'} mono /></Panel>
        <Panel><KV label="Pass rate"  value={lastRun && lastRun.tests_run > 0 ? Math.round(100 * lastRun.passing / lastRun.tests_run) + '%' : '—'} valueColor={lastRun && lastRun.failing === 0 ? GRN : ORG} /></Panel>
        <Panel><KV label="Next run"   value={nextRun ? nextRun.slice(0, 10) : '—'} mono /></Panel>
      </div>
      <Panel>
        <SectionHead title="Testing runs" sub="Risk-based coverage" />
        {runs === null ? (
          <EmptyState line="Loading test runs..." />
        ) : runs.length === 0 ? (
          <EmptyState line="No tests scheduled yet" sub="Risk-based annual testing program covers WSPs, supervision, trade surveillance, comms, AML, sales practices, and financials. PLANNED: findings become remediation tasks tied to a WSP section. No testing runner exists." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Run at</div><div>Scope</div><div>Passing</div><div>Failing</div><div>Next</div>
            </div>
            {runs.slice(0, 30).map(r => (
              <div key={r.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div>{r.run_at.slice(0, 10)}</div>
                <div style={{ color: INK }}>{r.scope}</div>
                <div style={{ color: GRN }}>{r.passing}</div>
                <div style={{ color: r.failing > 0 ? RED : INK_DIM }}>{r.failing}</div>
                <div>{r.next_run.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function RegBiSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('reg bi')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"Reg BI / suitability"} sub={"SEC · Best Interest care obligation"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No reg bi filings scheduled" sub={"Every recommendation is captured with the care factors considered, alternatives evaluated, and cost/complexity analysis — retained per Rule 15l-1."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function ComplaintsSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const complaints = (data?.complaints as Array<{ id: string; received_at: string; customer_name: string; channel?: string; category?: string; product?: string; nature?: string; status: string; summary?: string; description?: string; resolved_at?: string }> | undefined) ?? null;
  const openCount = complaints?.filter(c => c.status === 'open' || c.status === 'investigating').length ?? null;
  const escalated = complaints?.filter(c => c.status === 'escalated').length ?? null;
  const fmt = (n: number | null) => n === null ? '—' : String(n);

  const [open,     setOpen]     = useState(false);
  const [customer, setCustomer] = useState('');
  const [product,  setProduct]  = useState('');
  const [nature,   setNature]   = useState('');
  const [received, setReceived] = useState('');
  const [desc,     setDesc]     = useState('');
  const w = useWriter();

  const submit = () => {
    if (!customer.trim()) { w.reject('Customer name is required.'); return; }
    if (!product.trim())  { w.reject('Product or service complained of is required.'); return; }
    if (!nature.trim())   { w.reject('Nature of the complaint is required — it drives the 4530 problem code.'); return; }
    if (!desc.trim())     { w.reject('A description is required.'); return; }
    void w.run(async () => {
      const body: Record<string, unknown> = {
        customer_name: customer.trim(),
        product:       product.trim(),
        nature:        nature.trim(),
        description:   desc.trim(),
      };
      // Receipt date matters: the 4530 reporting clock runs from it.
      if (received.trim()) body.received_at = received.trim();
      await apiPost<{ complaint?: unknown }>('/api/compliance/complaints/create', body);
      setCustomer(''); setProduct(''); setNature(''); setReceived(''); setDesc('');
      invalidate('complaints', 'overview');
      return 'Complaint logged and chain-anchored.';
    });
  };

  return (
    <>
      <SectionHead title="Complaint log" sub="FINRA Rule 4530 — customer complaints" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Total complaints" value={fmt(complaints?.length ?? null)} /></Panel>
        <Panel><KV label="Open"             value={fmt(openCount)} valueColor={(openCount ?? 0) > 0 ? ORG : GRN} /></Panel>
        <Panel><KV label="Escalated"        value={fmt(escalated)} valueColor={(escalated ?? 0) > 0 ? RED : GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead
          title="Complaint queue"
          right={open ? '' : <ActionBtn onClick={() => { w.clear(); setOpen(true); }}>+ Log complaint</ActionBtn>}
        />
        {open && (
          <FormShell
            title="Log a customer complaint"
            note="Chain-anchored on receipt. Quarterly 4530(d) statistical reporting to FINRA is NOT performed by this system — complaints logged here are not transmitted anywhere."
            busy={w.busy} error={w.error} ok={w.ok} submitLabel="Log complaint"
            onSubmit={submit}
            onCancel={() => { setOpen(false); w.clear(); }}
          >
            <FormGrid cols={3}>
              <TextField label="Customer name (required)" value={customer} onChange={setCustomer} placeholder="Complainant" />
              <TextField label="Product / service (required)" value={product} onChange={setProduct} placeholder="e.g. equities, margin, options" />
              <TextField label="Received on" value={received} onChange={setReceived} kind="date" hint="Defaults to today. The 4530 clock runs from this date." />
            </FormGrid>
            <FormGrid cols={1}>
              <TextField label="Nature (required)" value={nature} onChange={setNature} placeholder="e.g. unauthorized trading, unsuitable recommendation, fees" />
            </FormGrid>
            <AreaField label="Description (required)" value={desc} onChange={setDesc} rows={5} placeholder="What the customer alleges, in their terms." />
          </FormShell>
        )}
        {complaints === null ? (
          <EmptyState line="Loading complaints..." />
        ) : complaints.length === 0 ? (
          <EmptyState line="No complaints on file" sub="Use “+ Log complaint” above to record one. Every complaint is chain-anchored on receipt. There is NO automated ingest from email, phone or regulator inquiry, and NO quarterly 4530(d) statistical reporting — complaints logged here are not transmitted anywhere." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12, marginTop: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Received</div><div>Customer</div><div>Product</div><div>Nature</div><div>Status</div><div>Ref</div>
            </div>
            {complaints.slice(0, 40).map(c => (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div>{c.received_at ? c.received_at.slice(0, 10) : '—'}</div>
                <div style={{ color: INK }}>{c.customer_name}</div>
                <div>{c.product ?? c.channel ?? '—'}</div>
                <div>{(c.nature ?? c.category ?? '—').replace('_', ' ')}</div>
                <div style={{ color: c.status === 'escalated' ? RED : c.status === 'resolved' ? GRN : ORG }}>{(c.status ?? 'unknown').toUpperCase()}</div>
                <div style={{ opacity: 0.7 }}>{c.id.slice(0, 12)}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function FirmElementSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('firm element')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"Continuing education — Firm Element"} sub={"FINRA · annual training program"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No firm element filings scheduled" sub={"Annual firm-element training assigned per registration category. Completions chain-anchored for regulator retrieval."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function AnnualMtgSection({ data }: { data: Record<string, unknown> | null }) {
  const entries = (data?.calendar as Array<{ id: string; label: string; agency: string; due_at: string; cadence: string; status: string }> | undefined) ?? null;
  const mine = entries?.filter(e => e.label.toLowerCase().includes('annual')) ?? null;
  const now = Date.now();
  const nextUpcoming = mine?.filter(e => new Date(e.due_at).getTime() > now).sort((a, b) => a.due_at.localeCompare(b.due_at))[0] ?? null;
  const overdue = mine?.filter(e => new Date(e.due_at).getTime() < now && e.status !== 'filed').length ?? null;
  const filed = mine?.filter(e => e.status === 'filed').length ?? null;
  const daysUntilNext = nextUpcoming ? Math.floor((new Date(nextUpcoming.due_at).getTime() - now) / 86400_000) : null;
  return (
    <>
      <SectionHead title={"Annual compliance meeting"} sub={"FINRA · Rule 3110 annual meeting"} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Next filing"    value={nextUpcoming ? nextUpcoming.due_at.slice(0, 10) : '—'} valueColor={daysUntilNext !== null && daysUntilNext <= 7 ? ORG : undefined} /></Panel>
        <Panel><KV label="Days until"     value={daysUntilNext === null ? '—' : daysUntilNext + ' d'} mono valueColor={daysUntilNext !== null && daysUntilNext <= 3 ? RED : undefined} /></Panel>
        <Panel><KV label="Filed to date"  value={filed === null ? '—' : String(filed)} mono valueColor={GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Filing history" right={overdue !== null && overdue > 0 ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● {overdue} overdue</span> : ''} />
        {mine === null ? (
          <EmptyState line="Loading filings..." />
        ) : mine.length === 0 ? (
          <EmptyState line="No annual filings scheduled" sub={"CCO-led annual meeting attended by all registered personnel. Attendance and content chain-anchored."} />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Label</div><div>Cadence</div><div>Due</div><div>Status</div>
            </div>
            {mine.slice(0, 30).map(e => (
              <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '2fr auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div style={{ color: INK }}>{e.label}</div>
                <div>{e.cadence}</div>
                <div>{e.due_at.slice(0, 10)}</div>
                <div style={{ color: e.status === 'filed' ? GRN : e.status === 'overdue' ? RED : ORG }}>{e.status.toUpperCase()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function RestrictedSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const items = (data?.items as Array<{ id: string; symbol: string; reason: string; effective_at?: string; expires_at?: string; status?: string; added_by?: string }> | undefined) ?? null;

  const [open,    setOpen]    = useState(false);
  const [symbol,  setSymbol]  = useState('');
  const [reason,  setReason]  = useState('');
  const [addedBy, setAddedBy] = useState('');
  const [expires, setExpires] = useState('');
  const add = useWriter();
  const rm  = useWriter();
  const [pendingSymbol, setPendingSymbol] = useState<string | null>(null);

  const submit = () => {
    if (!symbol.trim()) { add.reject('Symbol is required.'); return; }
    if (!reason.trim()) { add.reject('A reason is required — an unexplained restriction cannot be defended in a review.'); return; }
    void add.run(async () => {
      const body: Record<string, unknown> = { symbol: symbol.trim().toUpperCase(), reason: reason.trim() };
      if (addedBy.trim()) body.added_by   = addedBy.trim();
      if (expires.trim()) body.expires_at = expires.trim();
      await apiPost<{ item?: unknown }>('/api/compliance/restricted', body);
      setSymbol(''); setReason(''); setExpires('');
      invalidate('restricted', 'emp-trading');
      return 'Symbol added to the restricted list.';
    });
  };

  const remove = (sym: string) => {
    if (!window.confirm(`Remove ${sym} from the restricted list? Employee and proprietary trading in it will no longer be blocked.`)) return;
    setPendingSymbol(sym);
    void rm.run(async () => {
      await apiPost<{ removed?: unknown }>('/api/compliance/restricted', { symbol: sym, remove: true });
      setPendingSymbol(null);
      invalidate('restricted', 'emp-trading');
      return `${sym} removed from the restricted list.`;
    }).then(done => { if (!done) setPendingSymbol(null); });
  };

  return (
    <>
      <SectionHead
        title="Restricted list"
        sub="Information barriers + trading restrictions"
        right={open ? '' : <ActionBtn onClick={() => { add.clear(); setOpen(true); }}>+ Add restricted symbol</ActionBtn>}
      />
      <Panel>
        {open && (
          <FormShell
            title="Add restricted symbol"
            note="While a symbol is restricted, employee and firm proprietary trading in it should be blocked. Record why — MNPI possession, IPO participation, research coverage, deal involvement or a blackout window."
            busy={add.busy} error={add.error} ok={add.ok} submitLabel="Add to restricted list"
            onSubmit={submit}
            onCancel={() => { setOpen(false); add.clear(); }}
          >
            <FormGrid cols={4}>
              <TextField label="Symbol (required)" value={symbol} onChange={setSymbol} placeholder="AAPL" />
              <TextField label="Reason (required)" value={reason} onChange={setReason} placeholder="MNPI — pending transaction" />
              <TextField label="Added by"          value={addedBy} onChange={setAddedBy} placeholder="Initials or user ID" />
              <TextField label="Expires"           value={expires} onChange={setExpires} kind="date" hint="Leave blank for no scheduled expiry." />
            </FormGrid>
          </FormShell>
        )}
        <FormMsg error={rm.error} ok={rm.error ? null : rm.ok} />
        {items === null ? (
          <EmptyState line="Loading restricted list..." />
        ) : items.length === 0 ? (
          <EmptyState line="Restricted list is empty" sub="Use “+ Add restricted symbol” above. Symbols on the restricted list block employee trading and firm proprietary trading during the restriction window — MNPI possession, IPO participation, research coverage, deal involvement, blackout windows." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12, marginTop: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Symbol</div><div>Reason</div><div>Effective</div><div>Expires</div><div>Status</div><div>Action</div>
            </div>
            {items.slice(0, 60).map(i => (
              <div key={i.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)', alignItems: 'center' }}>
                <div style={{ color: INK }}>{i.symbol}</div>
                <div>
                  {i.reason}
                  {i.added_by && <span style={{ color: INK_FAINT }}> · {i.added_by}</span>}
                </div>
                <div>{i.effective_at ? i.effective_at.slice(0, 10) : '—'}</div>
                <div>{i.expires_at ? i.expires_at.slice(0, 10) : '—'}</div>
                <div style={{ color: i.status === 'active' ? ORG : i.status === 'expired' ? INK_DIM : GRN }}>{(i.status ?? 'unknown').toUpperCase()}</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionBtn size="sm" tone="bad" disabled={rm.busy} onClick={() => remove(i.symbol)}>
                    {rm.busy && pendingSymbol === i.symbol ? '…' : 'REMOVE'}
                  </ActionBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function PositionReconSection({ data }: { data: Record<string, unknown> | null }) {
  const snapshots = (data?.snapshots as Array<{ id: string; run_at: string; kind: string; broker: string; in_agreement: boolean; breaks_count: number; breaks_hash: string }> | undefined) ?? null;
  const positionSnaps = snapshots?.filter(s => s.kind === 'position') ?? null;
  const last = positionSnaps && positionSnaps.length > 0 ? positionSnaps[0] : null;
  return (
    <>
      <SectionHead title="Position reconciliation" sub="Daily broker vs. book comparison" right={last && last.in_agreement ? <span style={{ color: GRN, fontFamily: MONO, fontSize: 11 }}>● In agreement</span> : (last ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● Breaks</span> : '')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Last run"   value={last ? last.run_at.slice(0, 16).replace('T', ' ') : '—'} mono /></Panel>
        <Panel><KV label="Broker"     value={last?.broker ?? '—'} /></Panel>
        <Panel><KV label="Open breaks" value={last === null ? '—' : String(last.breaks_count)} valueColor={last && last.breaks_count > 0 ? RED : GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Recent snapshots" />
        {positionSnaps === null ? (
          <EmptyState line="Loading snapshots..." />
        ) : positionSnaps.length === 0 ? (
          <EmptyState line="No reconciliation snapshots yet" sub="PLANNED: nightly reconciliation of broker positions against the book of record. A reconciliation job does exist elsewhere, but it is NOT connected to this view; breaks are chain-anchored with SHA-256 fingerprint and routed to the operations queue for T+1 resolution." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto 1fr', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Run at</div><div>Broker</div><div>In agreement</div><div>Breaks</div><div>Fingerprint</div>
            </div>
            {positionSnaps.slice(0, 30).map(s => (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto 1fr', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div>{s.run_at.slice(0, 16).replace('T', ' ')}</div>
                <div>{s.broker}</div>
                <div style={{ color: s.in_agreement ? GRN : RED }}>{s.in_agreement ? 'YES' : 'NO'}</div>
                <div style={{ color: s.breaks_count > 0 ? RED : INK_DIM }}>{s.breaks_count}</div>
                <div style={{ opacity: 0.65 }}>{s.breaks_hash.slice(0, 24)}…</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function CashReconSection({ data }: { data: Record<string, unknown> | null }) {
  const snapshots = (data?.snapshots as Array<{ id: string; run_at: string; kind: string; broker: string; in_agreement: boolean; breaks_count: number; breaks_hash: string }> | undefined) ?? null;
  const cashSnaps = snapshots?.filter(s => s.kind === 'cash') ?? null;
  const last = cashSnaps && cashSnaps.length > 0 ? cashSnaps[0] : null;
  return (
    <>
      <SectionHead title="Cash reconciliation" sub="Daily broker cash vs. book" right={last && last.in_agreement ? <span style={{ color: GRN, fontFamily: MONO, fontSize: 11 }}>● In agreement</span> : (last ? <span style={{ color: RED, fontFamily: MONO, fontSize: 11 }}>● Breaks</span> : '')} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        <Panel><KV label="Last run"    value={last ? last.run_at.slice(0, 16).replace('T', ' ') : '—'} mono /></Panel>
        <Panel><KV label="Broker"      value={last?.broker ?? '—'} /></Panel>
        <Panel><KV label="Open breaks" value={last === null ? '—' : String(last.breaks_count)} valueColor={last && last.breaks_count > 0 ? RED : GRN} /></Panel>
      </div>
      <Panel>
        <SectionHead title="Recent snapshots" />
        {cashSnaps === null ? (
          <EmptyState line="Loading snapshots..." />
        ) : cashSnaps.length === 0 ? (
          <EmptyState line="No cash reconciliation snapshots yet" sub="Nightly cash reconciliation across all funding accounts (customer cash, firm cash, PAB, reserve, sweep). Breaks routed to ops queue; chain-anchored fingerprints on every snapshot." />
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto 1fr', gap: 12, color: INK_FAINT, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: 8, borderBottom: '1px solid ' + LINE }}>
              <div>Run at</div><div>Broker</div><div>In agreement</div><div>Breaks</div><div>Fingerprint</div>
            </div>
            {cashSnaps.slice(0, 30).map(s => (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto 1fr', gap: 12, padding: '8px 0', color: INK_DIM, borderBottom: '1px solid rgba(68,64,60,0.4)' }}>
                <div>{s.run_at.slice(0, 16).replace('T', ' ')}</div>
                <div>{s.broker}</div>
                <div style={{ color: s.in_agreement ? GRN : RED }}>{s.in_agreement ? 'YES' : 'NO'}</div>
                <div style={{ color: s.breaks_count > 0 ? RED : INK_DIM }}>{s.breaks_count}</div>
                <div style={{ opacity: 0.65 }}>{s.breaks_hash.slice(0, 24)}…</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

// ── Section dispatcher ─────────────────────────────────────────────
// `invalidate` is how a write gets its section refetched: it drops the
// cached key and the loader effect picks it up again.
// ── Trading kill switch (admin) ────────────────────────────────────
// Surfaces the firm-wide trading halt (GET/POST /api/admin/terminal/kill).
// This is the one control on this workstation that stops live order flow:
// equities, options AND event contracts all gate on the same getKill()
// before an order is accepted, so a halt here is authoritative across the
// whole desk. It is also what the auto-trip writes to when the venue-reject
// rate breaches its ceiling — the same state, surfaced for a human.
//
// Gated on terminal:risk server-side; the UI mirrors that so a
// compliance-only operator sees the state but is not shown an actionable
// button the server would reject. The halt-state read is NEVER faked: a
// failed load renders the shared DATA UNAVAILABLE banner above, and this
// panel refuses to draw a green "LIVE" it did not actually read.
interface KillState {
  on?:             boolean;
  reason?:         string | null;
  set_by?:         string | null;
  set_at?:         string | null;
  halted_symbols?: string[];
}

function KillSwitchSection({ data, invalidate }: { data: Record<string, unknown> | null; invalidate: Invalidate }) {
  const hasEnt = useStore(s => s.hasEnt);
  const canControl = hasEnt('terminal:risk') || hasEnt('admin:platform');
  const w = useWriter();
  const [reason,  setReason]  = useState('');
  const [confirm, setConfirm] = useState<null | 'halt' | 'resume'>(null);

  const kill = data as KillState | null;
  // Distinguish "loaded, currently off" from "not yet read". The loader
  // stores the GET result on success and sets _unavailable on failure (a
  // loud banner renders above this panel). Until `on` is an actual boolean
  // we have NOT confirmed the desk is live, so we must not imply it is.
  const unavailable = typeof (data as { _unavailable?: string } | null)?._unavailable === 'string';
  const loaded = !unavailable && kill !== null && typeof kill.on === 'boolean';
  const halted = loaded && kill!.on === true;
  const haltedSymbols = (loaded && Array.isArray(kill!.halted_symbols)) ? kill!.halted_symbols! : [];

  const flip = (on: boolean) => {
    if (on && reason.trim().length < 4) {
      w.reject('Enter a reason for the halt (min 4 characters). It is written to the audit chain and shown to every trader whose order is refused.');
      return;
    }
    void w.run(async () => {
      // POST { on, reason }. On resume, reason is omitted. The switch flips
      // server-side; we then invalidate so the panel re-reads the real state
      // rather than assuming the write's intent took effect.
      await apiPost('/api/admin/terminal/kill', on ? { on: true, reason: reason.trim() } : { on: false });
      setConfirm(null);
      if (!on) setReason('');
      invalidate('kill-switch');
      return on
        ? 'Trading HALTED firm-wide. All new orders — equities, options and event contracts — are now refused until resumed.'
        : 'Trading RESUMED. Order flow is live again across the desk.';
    });
  };

  return (
    <>
      <SectionHead
        title="Trading halt — firm-wide kill switch"
        sub="Internal · authoritative across equities, options and event contracts"
        right={loaded ? (halted ? 'HALTED' : 'LIVE') : ''}
      />

      {/* State banner. Only ever asserts LIVE/HALTED when the state was
          actually read; otherwise it says so plainly. */}
      <Panel style={{
        marginBottom: 20,
        border: '1px solid ' + (halted ? RED : loaded ? LINE : LINE),
        background: halted ? 'rgba(198,107,107,0.08)' : PANEL,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 12, height: 12, borderRadius: '50%',
            background: !loaded ? INK_FAINT : halted ? RED : GRN,
            boxShadow: loaded ? `0 0 0 4px ${halted ? 'rgba(198,107,107,0.18)' : 'rgba(105,176,120,0.18)'}` : 'none',
            flexShrink: 0,
          }} />
          <div>
            <div style={{ fontFamily: FONT, fontSize: 20, fontWeight: 300, letterSpacing: '-0.01em', color: !loaded ? INK_FAINT : halted ? RED : GRN }}>
              {!loaded ? 'Halt state not read' : halted ? 'Trading is HALTED' : 'Trading is LIVE'}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: INK_DIM, marginTop: 4, lineHeight: 1.5 }}>
              {!loaded
                ? 'The desk kill state could not be confirmed. Do NOT assume order flow is live — see the notice above.'
                : halted
                  ? 'Every new order across the firm is being refused at the router before it reaches a venue.'
                  : 'Orders are accepted subject to each account’s own BYOK credentials and per-symbol halts.'}
            </div>
          </div>
        </div>

        {halted && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 18, paddingTop: 16, borderTop: '1px solid ' + LINE }}>
            <KV label="Reason"    value={kill?.reason || '—'} mono />
            <KV label="Set by"    value={kill?.set_by || '—'} mono />
            <KV label="Halted at" value={kill?.set_at ? kill.set_at.replace('T', ' ').slice(0, 19) + ' UTC' : '—'} mono />
          </div>
        )}

        {loaded && haltedSymbols.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid ' + LINE }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK_FAINT, marginBottom: 8 }}>
              Per-symbol halts ({haltedSymbols.length}) — active even while the firm-wide switch is off
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {haltedSymbols.map(s => (
                <span key={s} style={{ fontFamily: MONO, fontSize: 11, color: ORG, padding: '2px 9px', border: '1px solid ' + LINE, borderRadius: 3 }}>{s}</span>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {/* Control. Hidden entirely for operators without the risk
          entitlement — the server enforces it too, but showing a button
          that always 403s is a worse experience than an honest note. */}
      {!canControl ? (
        <Panel>
          <EmptyState
            line="Halt and resume require the terminal:risk entitlement"
            sub="You can see the current desk state above, but flipping the firm-wide switch is restricted to risk/admin operators. Ask a risk operator, or have admin:platform granted."
          />
        </Panel>
      ) : !loaded ? (
        <Panel>
          <EmptyState
            line="Controls are disabled until the halt state is read"
            sub="Toggling the switch from an unknown state risks halting a live desk or resuming a halted one without knowing which. Resolve the read error above, then reload."
          />
        </Panel>
      ) : halted ? (
        <FormShell
          title="Resume trading"
          note="Lifts the firm-wide halt. Orders will be accepted again immediately (subject to per-symbol halts, which are NOT cleared by this action). The resume is written to the audit chain."
          busy={w.busy} error={w.error} ok={w.ok} submitLabel="Resume — confirm"
          onSubmit={() => { if (confirm === 'resume') flip(false); else { w.clear(); setConfirm('resume'); } }}
          onCancel={confirm === 'resume' ? () => setConfirm(null) : undefined}
        >
          {confirm === 'resume'
            ? <div style={{ fontFamily: MONO, fontSize: 11, color: ORG, lineHeight: 1.55 }}>Confirm: lift the firm-wide halt and let orders flow again. Click "Resume — confirm" once more.</div>
            : <div style={{ fontFamily: MONO, fontSize: 11, color: INK_DIM, lineHeight: 1.55 }}>The desk is currently halted. Resuming re-enables live order flow across every account.</div>}
        </FormShell>
      ) : (
        <FormShell
          title="Halt all trading"
          note="Stops every new order across the firm at the router — equities, options and event contracts alike. Resting orders are NOT cancelled by this switch; it prevents new order acceptance. Use it for a suspected credential compromise, a runaway strategy, or a regulatory stop."
          busy={w.busy} error={w.error} ok={w.ok} submitLabel={confirm === 'halt' ? 'HALT NOW — confirm' : 'Halt all trading'}
          onSubmit={() => { if (confirm === 'halt') flip(true); else { if (reason.trim().length < 4) { w.reject('Enter a reason for the halt (min 4 characters).'); return; } w.clear(); setConfirm('halt'); } }}
          onCancel={confirm === 'halt' ? () => setConfirm(null) : undefined}
        >
          <AreaField
            label="Reason (required — written to the audit chain)"
            value={reason}
            onChange={setReason}
            rows={2}
            placeholder="e.g. Suspected key compromise on desk 3 — halting pending rotation"
            hint="Shown to every trader whose order is refused while the halt is active."
          />
          {confirm === 'halt' && (
            <div style={{ fontFamily: MONO, fontSize: 11, color: RED, lineHeight: 1.55, marginTop: 4 }}>
              Confirm: halt ALL new order flow firm-wide, now. Click "HALT NOW — confirm" once more.
            </div>
          )}
        </FormShell>
      )}
    </>
  );
}

function renderSection(id: string, data: Record<string, unknown> | null, invalidate: Invalidate): React.ReactNode {
  switch (id) {
    case 'overview':       return <Overview       data={data} invalidate={invalidate} />;
    case 'kill-switch':    return <KillSwitchSection data={data} invalidate={invalidate} />;
    case 'cat':            return <CATSection     data={data} />;
    case 'rule606':        return <Rule606Section data={data} />;
    case 'rule605':        return <Rule605Section data={data} />;
    case 'focus':          return <FocusSection   data={data} />;
    case 'form-crs':       return <FormCrsSection data={data} />;
    case 'trade-surv':     return <TradeSurvSection data={data} />;
    case 'comms-surv':     return <CommsSurvSection data={data} />;
    case 'emp-trading':    return <EmpTradingSection data={data} invalidate={invalidate} />;
    case 'ofac':           return <OfacSection    data={data} invalidate={invalidate} />;
    case 'cip-kyc':        return <CipKycSection  data={data} invalidate={invalidate} />;
    case 'sar':            return <SarSection     data={data} invalidate={invalidate} />;
    case 'reg-sho':        return <RegShoSection  data={data} />;
    case 'close-out':      return <CloseOutSection data={data} />;
    case 'archive-17a4':   return <ArchiveSection data={data} />;
    case 'books-17a3':     return <BooksSection   data={data} />;
    case 'blue-sheets':    return <BlueSheetsSection data={data} />;
    case 'wsps':           return <WspsSection    data={data} invalidate={invalidate} />;
    case 'testing':        return <TestingSection data={data} />;
    case 'reg-bi':         return <RegBiSection   data={data} />;
    case 'complaints':     return <ComplaintsSection data={data} invalidate={invalidate} />;
    case 'firm-element':   return <FirmElementSection data={data} />;
    case 'annual-mtg':     return <AnnualMtgSection data={data} />;
    case 'restricted':     return <RestrictedSection data={data} invalidate={invalidate} />;
    case 'position-recon': return <PositionReconSection data={data} />;
    case 'cash-recon':     return <CashReconSection data={data} />;
    default:               return <Overview data={data} invalidate={invalidate} />;
  }
}

// ── Sidebar ────────────────────────────────────────────────────────

function Sidebar({ active, onPick }: { active: string; onPick: (id: string) => void }) {
  // Insert tier separators when tier changes between adjacent groups so
  // the sidebar reads Foundation vs BD as explicit sections.
  const rendered: React.ReactNode[] = [];
  let lastTier: 'FOUNDATION' | 'BROKER-DEALER' | null = null;
  SECTIONS.forEach(group => {
    if (group.tier !== lastTier) {
      rendered.push(
        <div key={'tier-' + group.tier} style={{
          padding: '18px 18px 6px', margin: lastTier ? '10px 0 0' : '0',
          borderTop: lastTier ? '1px solid ' + LINE : 'none',
        }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.14em', color: group.tier === 'FOUNDATION' ? ORG : '#d4a45a', fontWeight: 500 }}>
            {group.tier === 'FOUNDATION' ? 'FOUNDATION · CROSS-VERTICAL' : 'BROKER-DEALER · VERTICAL'}
          </div>
        </div>
      );
      lastTier = group.tier;
    }
    rendered.push(
      <div key={group.group} style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK_FAINT, padding: '0 18px 6px' }}>{group.group}</div>
        {group.items.map(item => (
          <button
            key={item.id}
            onClick={() => onPick(item.id)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '7px 18px', border: 'none',
              background: active === item.id ? PANEL_HI : 'transparent',
              borderLeft: active === item.id ? '2px solid ' + ORG : '2px solid transparent',
              color: active === item.id ? INK : INK_DIM,
              fontFamily: FONT, fontSize: 12.5, cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span>{item.label}</span>
            {item.agency && <AgencyChip agency={item.agency} />}
          </button>
        ))}
      </div>
    );
  });
  return (
    <div style={{
      width: 240, flexShrink: 0,
      borderRight: '1px solid ' + LINE,
      background: 'rgba(0,0,0,0.15)',
      padding: '0 0 20px',
      overflowY: 'auto',
    }}>
      {rendered}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────

export function Compliance() {
  const hasEnt = useStore(s => s.hasEnt);
  const hasCompliance = hasEnt('terminal:compliance') || hasEnt('admin:platform');
  const [section, setSection] = useState('overview');
  const [sectionData, setSectionData] = useState<Record<string, Record<string, unknown> | null>>({});

  // A successful write drops the cached copy of every section it touched;
  // the loader effect below then refetches whichever one is on screen, and
  // the rest reload when navigated to. Only ever called from an event
  // handler, and the effect's already-loaded guard runs before any state
  // write, so this cannot drive the effect into a loop.
  const invalidate: Invalidate = (...sections: string[]) => {
    setSectionData(prev => {
      const next = { ...prev };
      for (const s of sections) delete next[s];
      return next;
    });
  };

  useEffect(() => {
    if (!hasCompliance) return;
    const h = authHeaders();
    // Cross-origin API base — Terminal SPA is served from
    // terminal.omnistrat.ai, the compliance API lives on api.omnistrat.ai.
    // Same host as ohlcv/portfolio/orders — see lib/api.ts.
    // Was VITE_API_BASE_URL — a name nothing defines. lib/api.ts reads
    // VITE_API_BASE, so this always fell through to the hardcoded prod
    // host and every dev/staging build silently pointed Compliance at
    // production. Use the one shared constant instead of a second,
    // slightly-different copy of the same logic.
    const API = API_BASE;
    // Section → endpoint mapping matches the router paths defined in
    // src/api/complianceRoutes.ts. Missing endpoints stay `null` and
    // render honest empty states — no fabricated numbers anywhere.
    const endpoints: Record<string, string | null> = {
      'overview':       '/api/compliance/overview',
      // Admin kill-switch state. Not a /api/compliance/* route — it reads
      // the same terminal:risk-gated store the trading desk and the
      // auto-trip write to, so the halt shown here is the real one. A
      // failed/unauthorised read falls through to the shared _unavailable
      // banner, which is correct: an unread halt state must not render as
      // "LIVE".
      'kill-switch':    '/api/admin/terminal/kill',
      'ofac':           '/api/compliance/ofac',
      'cip-kyc':        '/api/compliance/cip',
      'sar':            '/api/compliance/sar',
      'complaints':     '/api/compliance/complaints',
      'wsps':           '/api/compliance/wsps',
      'blue-sheets':    '/api/compliance/blue-sheets',
      'archive-17a4':   '/api/compliance/archive-17a4',
      'testing':        '/api/compliance/testing',
      'comms-surv':     '/api/compliance/comms-surv',
      'trade-surv':     '/api/compliance/trade-surv',
      'emp-trading':    '/api/compliance/emp-trading',
      'reg-sho':        '/api/compliance/reg-sho',
      'restricted':     '/api/compliance/restricted',
      'position-recon': '/api/compliance/reconciliation',
      'cash-recon':     '/api/compliance/reconciliation',
      // Sections without dedicated endpoints — read the shared
      // /calendar feed (upcoming deadlines) so the UI can still display
      // "next filing" state without fabricating data.
      'cat':            '/api/compliance/calendar',
      'rule606':        '/api/compliance/calendar',
      'rule605':        '/api/compliance/calendar',
      'focus':          '/api/compliance/calendar',
      'form-crs':       '/api/compliance/calendar',
      'firm-element':   '/api/compliance/calendar',
      'annual-mtg':     '/api/compliance/calendar',
      'close-out':      '/api/compliance/calendar',
      // books-17a3 and reg-bi were pointed at UNRELATED endpoints
      // (archive-17a4 and calendar). Their renderers read keys those
      // responses never contain, so both panels rendered permanently
      // blank while carrying a "live" badge. Left unmapped deliberately:
      // the loader now says "No endpoint is wired for this section yet",
      // which is true, instead of showing an empty table that implies a
      // completed check. Restore the mapping when the endpoints exist.
    };
    // Sentinel used when a fetch fails or is unauthorized. Every renderer
    // reads its own key (obligations / screenings / calendar / ...), so
    // seeding them all as empty makes a failed load render an honest empty
    // state instead of hanging on "Loading..." forever.
    const EMPTY: Record<string, unknown> = {
      obligations: [], calendar: [], screenings: [], customers: [], filings: [],
      complaints: [], policies: [], requests: [], archive: [], runs: [],
      alerts: [], locates: [], items: [], snapshots: [], meta: {},
    };
    // The already-loaded guard MUST come first. sectionData is in this
    // effect's dep array, so writing state before the guard re-runs the
    // effect, which writes again — an infinite loop that React kills with
    // "Maximum update depth exceeded" on any unmapped section.
    if (sectionData[section] !== undefined) return; // already loaded
    const ep = endpoints[section];
    if (!ep) { setSectionData(prev => ({ ...prev, [section]: { ...EMPTY, _unavailable: 'No endpoint is wired for this section yet.' } })); return; }
    (async () => {
      // A FAILED LOAD IS NOT A CLEAN COMPLIANCE RESULT.
      //
      // Seeding EMPTY on any error made a 401, a 502 or a dropped
      // connection indistinguishable from "we checked and found nothing":
      // the surveillance panel rendered Spoofing 0 / Layering 0 / Wash
      // trade 0 in green, and reconciliation reported "in agreement".
      // For a regulatory product that is the worst possible failure mode —
      // it tells a compliance officer they are clean when nobody looked.
      // Carry an explicit _unavailable marker so renderers can say so.
      try {
        const r = await fetch(API + ep, { headers: h });
        if (!r.ok) {
          // Say WHICH refusal. DESIGNATION_REQUIRED (ask an owner to designate
          // you), DESIGNATIONS_UNAVAILABLE / ENTITLEMENTS_UNAVAILABLE (503: we
          // could not tell) and a plain 403 call for different actions.
          let body: unknown = null;
          try { body = await r.json(); } catch { /* error body was not JSON */ }
          const d = describeApiError(r.status, body as RouterErrorBody | null);
          setSectionData(prev => ({ ...prev, [section]: {
            ...EMPTY,
            _unavailable: `${d.message} (HTTP ${r.status})`
              + (d.kind === 'auth' ? '' : ' This is NOT a clean result.'),
          } }));
          return;
        }
        const d = await r.json();
        setSectionData(prev => ({ ...prev, [section]: d }));
      } catch (e) {
        setSectionData(prev => ({ ...prev, [section]: {
          ...EMPTY,
          _unavailable: `Could not reach the compliance service (${(e as Error).message}). This is NOT a clean result.`,
        } }));
      }
    })();
  }, [section, hasCompliance, sectionData]);

  if (!hasCompliance) {
    return (
      <div style={{ padding: '40px 32px', color: INK, fontFamily: FONT, maxWidth: 900, margin: '0 auto' }}>
        <div style={{
          fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: INK_FAINT, marginBottom: 12,
        }}>Terminal &middot; Compliance</div>
        <h1 style={{ fontSize: 40, fontWeight: 200, letterSpacing: '-0.03em', margin: '0 0 20px', lineHeight: 1.1 }}>Compliance suite.</h1>
        <p style={{ fontSize: 15, color: INK_DIM, lineHeight: 1.65, margin: '0 0 32px', maxWidth: 720 }}>
          A full broker-dealer compliance platform &mdash; regulatory reporting, trade + communications surveillance, AML/KYC, Reg SHO, records retention, supervision, and personnel oversight. Every artifact cryptographically anchored to the same audit chain that powers your Attestation and Match Certificates. Regulators can verify integrity without touching your database.
        </p>
        <div style={{
          background: PANEL, border: '1px solid ' + LINE, borderRadius: 10,
          padding: '28px 32px', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.1em', color: INK_FAINT, textTransform: 'uppercase' }}>Add-on price</div>
            <div style={{ fontSize: 26, fontWeight: 300, letterSpacing: '-0.02em' }}>$2,500 <span style={{ fontSize: 12, color: INK_DIM }}>/ mo &middot; per BD entity</span></div>
          </div>
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid ' + LINE, fontSize: 12, color: INK_FAINT, fontFamily: MONO, letterSpacing: '0.02em' }}>
            Every workflow chain-anchored. Every artifact publicly verifiable. Every export self-authenticating.
          </div>
        </div>
        <a
          href="mailto:enterprise@omnistrat.ai?subject=Terminal%20Compliance%20-%20add-on"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', background: ORG, color: BG,
            border: 'none', borderRadius: 999, textDecoration: 'none',
            fontFamily: FONT, fontSize: 13, fontWeight: 500,
          }}
        >Talk to enterprise &rarr;</a>
      </div>
    );
  }

  const activeMeta = SECTIONS.flatMap(g => g.items).find(i => i.id === section);
  const data = sectionData[section] ?? null;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 36px)', background: BG, color: INK, fontFamily: FONT }}>
      <Sidebar active={section} onPick={setSection} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ marginBottom: 20, display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK_FAINT }}>Terminal &middot; Compliance</div>
            {activeMeta?.agency && <AgencyChip agency={activeMeta.agency} />}
            {activeMeta?.badge && <BadgeChip text={activeMeta.badge} />}
          </div>
          {/* Rendered ABOVE the section, not inside it. Every panel below
              draws zeros and "in agreement" from the empty sentinel, so
              the warning has to sit where it cannot be missed and cannot
              depend on each individual renderer honouring the flag. */}
          {/* NOT CONFIGURED is a distinct, louder failure than "fetch
              failed". The endpoint returned 200 with an empty list, and
              every panel below will happily render "0 violations" in green
              beside a sentence claiming the control is active. Nothing is
              running: there is no detector, no ingest, no cron writing
              that store. An absence of evidence is not a passing result,
              and for a regulated firm presenting it as one is the most
              damaging thing this screen could do. */}
          {data && (data as { _control?: { configured?: boolean; note?: string; producer?: string | null } })._control?.configured === false && (
            <div style={{
              border: '2px solid #b00', background: 'rgba(187,0,0,0.10)',
              padding: '12px 14px', marginBottom: 16, borderRadius: 3,
              fontFamily: MONO, fontSize: 11, color: '#ff6b6b', lineHeight: 1.55,
            }}>
              <strong>NOT CONFIGURED — NOTHING WAS CHECKED.</strong>
              <div style={{ marginTop: 5, color: INK_FAINT }}>
                {(data as { _control: { note: string } })._control.note}
                {' '}Any count of <strong>0</strong>, and any “clean”, “active”
                or “in agreement” wording below, reflects an <em>absent control</em>,
                not a completed review. Do not rely on this panel as evidence
                of supervision.
              </div>
            </div>
          )}
          {data && typeof (data as { _unavailable?: string })._unavailable === 'string' && (
            <div style={{
              border: '1px solid #b00', background: 'rgba(187,0,0,0.08)',
              padding: '10px 14px', marginBottom: 16, borderRadius: 3,
              fontFamily: MONO, fontSize: 11, color: '#ff6b6b', lineHeight: 1.5,
            }}>
              <strong>DATA UNAVAILABLE — DO NOT READ THE FIGURES BELOW AS A RESULT.</strong>
              <div style={{ marginTop: 4, color: INK_FAINT }}>
                {(data as { _unavailable: string })._unavailable}{' '}
                Counts shown as 0 and statuses shown as “in agreement” reflect
                a failed load, not a completed check.
              </div>
            </div>
          )}
          {renderSection(section, data, invalidate)}
          <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid ' + LINE, fontFamily: MONO, fontSize: 10, letterSpacing: '0.06em', color: INK_FAINT, textAlign: 'center' }}>
            Chain-anchored artifacts &middot; verify at api.omnistrat.ai/api/attest/verify/&lt;id&gt;
          </div>
        </div>
      </div>
    </div>
  );
}
